/**
 * Template creation Wizard (008 US1 T010, 001/002).
 * generate (AI image-to-image) → mask → submit. The "mask" step merges the former detect +
 * edit steps onto ONE page: a compact canvas toolbar (Auto-detect (on-device) / Draw
 * manually) sits above the MaskEditor and the step's commit sits in the `.actionbar` below
 * it. **Detection runs 100% on-device** via the `genz-engine` CV path (no cloud, no OpenAI);
 * only generate (AI image-to-image) + submit/approve hit the cloud.
 */
import { useGuideSafeUrl } from "../generate/whiteGuide.js";
import { createRef, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useServices } from "../app/context.js";
import {
  useWizardStore, confirmDiscardWizard, isWizardDirty, generationJobIds, geometryOf, SELECTABLE_VIEWS,
  type GeneratedImage, type RunView,
} from "../stores/wizard.js";
import { storeTemplateImage } from "../stores/imageStore.js";
import { MaskEditor, defaultBrandRect, type MaskBakeHandle, type MaskEditorProps } from "../editor/MaskEditor.js";
// F-5 — the geometry fallback + the banner that carries its fix.
import { GeometryProblemBanner } from "../editor/GeometryProblemBanner.js";
import { detectionNoticeMessage } from "../editor/geometryRepair.js";
import {
  syncBakedAssetsToCloud, hostingEnabled, HostingDisabledError, type BakedUploadPayload,
} from "../templates/bakedSyncCloud.js";
import { mergeViewRefs, type BakedAssetRefsMap } from "../templates/bakedSync.js";
import { useGreyThumb } from "../generate/useGreyThumb.js";
import { viewLabel } from "../generate/runs.js";
import { useSessionStore } from "../stores/session.js";
import type { AutoKeyVerdict } from "../editor/maskEditorTypes.js";
import { customerErrorText, errorDetail } from "../generate/errorCode.js";
import { canAccessAdmin } from "../admin/gating.js";
import { withTimeout, fetchWithTimeout, UPLOAD_TIMEOUT_MS } from "../templates/timeout.js";
import { useProductCategories } from "../templates/useProductCategories.js";
import { InlineRetry } from "../ui/InlineRetry.js";
import { CatalogSelect } from "../ui/CatalogSelect.js";
import { InlineCreateRow } from "../ui/InlineCreateRow.js";
import { Icon } from "../ui/Icon.js";
import { RemovableImage } from "../ui/RemovableImage.js";
import { ActionBar } from "../ui/ActionBar.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { Modal } from "../ui/Modal.js";
import { InfoHint } from "../ui/Tooltip.js";
import { ErrorNotice, RequiredFieldError } from "../ui/ErrorNotice.js";
import type { ImageInput } from "@genz/engine-bind";
import { geometry } from "@genz/engine-bind";
import { higgsfieldMcp, HttpError, IMAGE_ACCEPT, type PickedFile, type PhoneModelOption, type ProductTemplate, type TemplateStatus, type ResolvedOptionGroup, type GenerationJobView } from "@genz/platform";
import { mayGenerateOnThisComputer, resolveGenerationSource } from "../stores/generationSource.js";
import { clearStuckDeviceRun, isStuckOnUnknownSubmission, runDeviceGeneration, storageAvailable } from "../generate/runDeviceGeneration.js";
import { fetchResultBytes, toDataUri, useObjectUrl } from "../generate/candidates.js";
import { CaseFlow } from "./CaseFlow.js";
import { ProductPickers } from "./ProductPickers.js";
import { BaseTypePicker, StylePicker, useCaseStyles } from "./DetailsPickers.js";
import { usePhoneCatalog } from "./usePhoneCatalog.js";
import { canApprove, canRetry } from "./caseJobState.js";
import { saveStatusCopy } from "../templates/saveStatusCopy.js";
// WS-B B-2: the shared Model label, so the wizard's dropdown reads like every other one.
// The wizard prints the same nouns the template surfaces do, so it asks the same rules:
// `modelLabel` for a phone model, `outputFormatLabel` for a format and `humanize` for a view
// type. It kept private capitalisers for the last two until review-1 m-3.
import { humanize, modelLabel, outputFormatLabel } from "../templates/templateLabels.js";
// Owner decision 2026-09-24: the customer's Details reuse the Generate page's Brand picker.
// WS-B B-8 / F-40: a catalog entry created or renamed from in here changes the SERVER-resolved
// `display.model` / `display.style` of every template that references it, so the list has to
// be told to re-ask rather than keep painting what it last fetched.
import { notifyCatalogChanged } from "../templates/catalogInvalidation.js";
// LB-1: `toWireMasks` was a second, DRIFTED copy here — it dropped a cutout's `corner_radius`, so
// a radius authored in the wizard was lost on the first save. Both surfaces share one serializer.
import { toWireMasks } from "../templates/wireMasks.js";
import { brandColorPrimary, resolveBackgroundColor } from "../templates/backgroundColor.js";
import { composeBackgroundColor } from "@genz/engine-bind";
import "./wizard.css";

/** Output formats the Wizard offers (cloud render_settings formats). */
const OUTPUT_FORMATS = ["png", "jpg", "webp"] as const;
/** Product categories (product_type) the Wizard offers (shared with the editor). */

const CASE_STEPS = [
  { key: "case", label: "Case photos" },
  { key: "clear", label: "Stage 1 · Clear case" },
  { key: "magenta", label: "Stage 2 · Print guide" },
  { key: "mask", label: "Mask" },
  { key: "submit", label: "Submit" },
];
/** A2 — a run the Generate page opened: the image is already made. */
const GENERATE_PAGE_STEPS = [
  { key: "generate", label: "Generate" },
  { key: "mask", label: "Place design" },
  { key: "submit", label: "Publish" },
];

/** A4 review — how long a customer waits for the automatic key before the veil ends. */
export const AUTOKEY_TIMEOUT_MS = 20_000;
/** What an unanswered key or an unreadable image reports: the customer sees GEN-COVERAGE. */
const AUTOKEY_UNREADABLE = "autokey_not_found: the image could not be read";

/** Max per-reference upload size (32 MB), shared by the picker and drag-drop. */
const MAX_REFERENCE_BYTES = 32 * 1024 * 1024;

/** ED-18 — caps on the two unbounded network waits in `submit()`. Neither had a timeout, so a
 *  wedged connection left the button on "Saving…" (and "Uploading template assets…") forever with
 *  no way out but restarting the app. The asset upload is best-effort (a timeout just warns); the
 *  submit itself surfaces the timeout as a normal submit failure the operator can retry. */
const ASSET_SYNC_TIMEOUT_MS = 120_000;
const SUBMIT_TIMEOUT_MS = 60_000;

/**
 * F-d — what a REFUSED publish actually keeps, in both refusal banners, from one string so the
 * two cannot drift apart.
 *
 * The copy this replaces said "the draft still holds every setting on this screen", which is
 * false and dangerous in the one direction that matters: `useWizardStore` is plain in-memory
 * zustand with NO persistence, and the server draft only ever received the code, the catalog
 * metadata (`createTemplate`) and the render settings (`patchRenderSettings`). The masks, the
 * chosen views, the brand-logo box, the case photo and every editor slider live in this tab and
 * nowhere else. An operator who believed the old sentence and closed the tab lost exactly the
 * work the refusal existed to protect.
 */
const REFUSAL_SURVIVES_COPY =
  "Your settings stay on this screen until you press Retry — do not close this tab. " +
  "The draft on the server holds the code and the output settings.";

/**
 * Decode a `data:<mediaType>;base64,...` data-URI into its raw bytes + mediaType.
 * The generate endpoint returns the image as a base64 data-URI; the detect/refine
 * path needs the decoded bytes as the case image.
 */
function decodeDataUri(dataUri: string): { bytes: Uint8Array; mediaType: string } {
  const match = /^data:([^;]+);base64,(.*)$/s.exec(dataUri);
  if (!match) throw new Error("Generated image was not a base64 data-URI.");
  const [, mediaType, b64] = match;
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return { bytes, mediaType };
}

/** A customer reads our own sentences, or else a code; an admin also the status or the vendor's text. */
function generateErrorMessage(e: unknown, admin: boolean): string {
  if (e instanceof HttpError) {
    if (e.status === 400) return "Those instructions were rejected by the AI guardrails. Rephrase and try again.";
    if (e.status === 413) return "One of the images is too large. Use a smaller image (up to 32 MB).";
    if (e.status === 403) return "Image generation needs an active activation. Activate a provider and try again.";
    if (e.status === 422) return "The generate request was invalid. Check both images and the instructions.";
  }
  if (!admin) return customerErrorText(errorDetail(e), (e as { jobId?: string } | null)?.jobId);
  if (e instanceof HttpError) return `Generation failed (${e.status}). Please try again.`;
  return e instanceof Error ? e.message : String(e);
}

/** Derive a readable template name from the uploaded reference filenames. */
function defaultTemplateName(refs: PickedFile[]): string {
  const base = refs.find((r) => /case/i.test(r.name))?.name ?? refs[0]?.name;
  const stem = base?.replace(/\.[a-z0-9]+$/i, "").replace(/[_-]+/g, " ").trim();
  return stem ? `Template — ${stem}` : `Template — ${new Date().toISOString().slice(0, 10)}`;
}

/** Turn a submit failure into a human message (stale token, geometry, offline). */
function submitErrorMessage(e: unknown): string {
  if (e instanceof HttpError) {
    if (e.status === 409) {
      // A 409 here is usually the version-token race, but the same submit() flow also
      // calls createTemplate() on first save — its code_conflict (the Code field) 409s
      // too, with a plain {detail: "template code already in use"} body (see
      // TemplateEditor.tsx's saveDetails, which reads e.body.detail the same way). Surface
      // the real reason instead of the generic version-race copy when we can tell them apart.
      const detail = (e.body as { detail?: string } | undefined)?.detail;
      if (detail === "template code already in use") {
        return "That code is already in use — pick a different one.";
      }
      return "This version changed elsewhere — reload and try again.";
    }
    if (e.status === 422) return "The masks failed server validation. Adjust the geometry and resubmit.";
    if (e.status === 403) return "You do not have permission to submit this template.";
    return `Submit failed (${e.status}). Please try again.`;
  }
  return e instanceof Error ? e.message : String(e);
}

/**
 * Optional host hooks. When the Wizard is launched from the Templates panel,
 * `onDone` returns to the list (re-fetching templates) and `onClose` cancels
 * the flow mid-way. Both are no-ops when the Wizard is used standalone, so its
 * internal upload→detect→edit→submit flow is unchanged.
 */
export interface WizardProps {
  onDone?: () => void;
  onClose?: () => void;
  /** A3 — the Generate page's choice for an image a check or the key has a note on: generate
   *  it again (paid; `question` names the cost) or reject it (free). */
  retake?: { question: string; regenerate: () => void; reject: () => void };
  /** The Generate page's job as the cloud has it once the publish is recorded on it. */
  onGenerationPublished?: (job: GenerationJobView) => void;
}

/** One removable thumbnail in an ImageStrip. */
function Thumb({ file, onRemove, testid }: { file: PickedFile; onRemove: () => void; testid: string }) {
  const url = useObjectUrl(file);
  return (
    <div className="wizard__thumb-item" data-testid={`${testid}-thumb`}>
      <RemovableImage src={url} alt={file.name} className="wizard__thumb"
        removeLabel={`Remove ${file.name}`} onRemove={onRemove} removeTestId={`${testid}-remove`} />
      <span className="wizard__thumb-name muted">{file.name}</span>
    </div>
  );
}

/**
 * Multi-image input slot for the generate step: a grid of removable thumbnails plus
 * an "add" dropzone. Both the reference and product slots accept MANY images.
 */
function ImageStrip({
  files,
  onAdd,
  onRemove,
  addLabel,
  testid,
}: {
  files: PickedFile[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  addLabel: string;
  testid: string;
}) {
  return (
    <div className="wizard__image-strip" data-testid={`${testid}-strip`}>
      {files.map((f, i) => (
        <Thumb key={`${f.name}-${i}`} file={f} onRemove={() => onRemove(i)} testid={testid} />
      ))}
      <button
        type="button"
        className="dropzone dropzone--add"
        onClick={onAdd}
        data-testid={`pick-${testid}`}
      >
        <span className="dropzone__icon" aria-hidden="true"><Icon name="upload" size={16}/></span>
        <strong>{files.length === 0 ? addLabel : "Add more"}</strong>
        <span className="muted">Any image — PNG, JPG, WebP, GIF, TIFF, HEIC… up to 32&nbsp;MB. Multiple allowed.</span>
      </button>
    </div>
  );
}

type RunKey = { verdict: AutoKeyVerdict; reason?: string };

/** A view a publish authors: its case photo, its editor's bake handle and its last baked payload. */
interface PublishTarget {
  view: string;
  bytes: Uint8Array | null;
  bake: RefObject<MaskBakeHandle | null>;
  payload: () => BakedUploadPayload | null;
}

/** A run view's thumbnail: its clear image, else its print guide in grey, never the magenta. */
function ViewThumb({ run, pressed, keyed, onPick }: {
  run: RunView; pressed: boolean; keyed?: AutoKeyVerdict; onPick: () => void;
}) {
  const clearFile = useMemo(
    () => (run.clear ? { bytes: run.clear, mime: run.image.clear?.mediaType } : undefined), [run],
  );
  const clearSrc = useObjectUrl(clearFile);
  const grey = useGreyThumb(`wizard:${run.generationJobId}`, run.clear ? null : run.image.dataUri);
  const src = clearSrc ?? grey;
  const unkeyed = keyed === "not-found" || keyed === "off-coverage";
  return (
    <li>
      <button type="button" className="wizard__view-thumb" aria-pressed={pressed} onClick={onPick}
        data-testid={`wizard-view-thumb-${run.view}`}>
        {src ? <img className="wizard__view-thumb-img" src={src} alt="" />
          : <span className="wizard__view-thumb-img" aria-hidden="true" />}
        <span className="caption">{viewLabel(run.view)}</span>
        {unkeyed && <span className="badge badge--plain badge--warn">Check print area</span>}
      </button>
    </li>
  );
}

/** One run view's editor. A view not on screen stays mounted, laid out but hidden, so it keeps
 *  its key and its edits and can bake on Publish. */
function RunViewCanvas({ run, active, veiled, editor }: {
  run: RunView; active: boolean; veiled: boolean; editor: Omit<MaskEditorProps, "caseImageUrl">;
}) {
  const file = useMemo(() => ({ bytes: run.image.bytes, mime: run.image.mediaType }), [run]);
  const caseImageUrl = useObjectUrl(file);
  return (
    <div className="wizard__canvas" data-veiled={active && veiled} data-inactive={!active || undefined}
      aria-hidden={!active || veiled || undefined} data-testid={active ? "wizard-canvas" : undefined}>
      <MaskEditor {...editor} caseImageUrl={caseImageUrl} inactive={!active} />
    </div>
  );
}

export function Wizard({ onDone, onClose, retake, onGenerationPublished }: WizardProps = {}) {
  const { io, engine, client, logger } = useServices();
  const wiz = useWizardStore();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // F-5 (a): what the post-detection geometry FALLBACK had to repair, said out loud. A repair
  // that changes the operator's geometry without telling them is the silent-failure class this
  // wave exists to remove — `role="status"`, warn tone, because the action SUCCEEDED.
  const [detectNotice, setDetectNotice] = useState<string | null>(null);
  // Base image pixel size (from the MaskEditor) → sizes the default 200×100px brand rectangle.
  const [baseDims, setBaseDims] = useState<{ w: number; h: number } | null>(null);
  // #1 — These creation fields live in the wizard STORE (not component state) so the
  // in-progress template survives navigation (nav away → back) and the discard guard
  // can reset them. Aliased to the same names so the rest of the component is unchanged.
  const prompt = wiz.prompt;
  const setPrompt = wiz.setPrompt;
  const brandArea = wiz.brandArea;
  const setBrandArea = wiz.setBrandArea;
  const phoneModelId = wiz.phoneModelId;
  const setPhoneModelId = wiz.setPhoneModelId;
  const caseStyleId = wiz.caseStyleId;
  const setCaseStyleId = wiz.setCaseStyleId;
  const baseType = wiz.baseType;
  const setBaseType = wiz.setBaseType;
  const code = wiz.code;
  const [codeRecovery, setCodeRecovery] = useState<{ suggestion?: string; changed: boolean } | null>(null);
  const [detailsAttempted, setDetailsAttempted] = useState(false);
  const detailsRef = useRef<HTMLFieldSetElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const setCode = (value: string) => {
    wiz.setCode(value);
    if (codeRecovery) {
      setCodeRecovery({ ...codeRecovery, changed: true });
      setError(null);
    }
  };
  const category = wiz.category;
  const setCategory = wiz.setCategory;
  const categories = useProductCategories(wiz.category);
  const outputFormat = wiz.outputFormat;
  const setOutputFormat = wiz.setOutputFormat;
  const background = wiz.background;
  const setBackground = wiz.setBackground;
  const backgroundHex = wiz.backgroundHex;
  const setBackgroundHex = wiz.setBackgroundHex;
  const views = wiz.views;
  const setViews = wiz.setViews;
  // Task 6: the Background option group (moved off the Generate surface). Fed by the same
  // resolved options the customer flow reads; the chosen choice KEY persists into the version
  // payload's render_settings.background on publish. Best-effort fetch — a failure just leaves
  // the picker empty (no background written).
  const [backgroundGroup, setBackgroundGroup] = useState<ResolvedOptionGroup | null>(null);
  /** WS-BG F2 — the brand's primary colour, for the LIVE mock-up preview only. The publish path
   *  re-reads the brand list itself (that read is the system of record). Best-effort and silent:
   *  a failure here costs a preview tint, and publish is what reports it. */
  const [previewBrandColor, setPreviewBrandColor] = useState<string | null>(null);
  useEffect(() => {
    if (background !== "brand_color") return;
    let cancelled = false;
    void (async () => {
      try {
        const c = brandColorPrimary(await client.listBrands());
        if (!cancelled) setPreviewBrandColor(c);
      } catch { /* preview-only — publish reports a brand colour it cannot resolve */ }
    })();
    return () => { cancelled = true; };
  }, [background, client]);
  /** The canvas the live preview composites on, from the LIVE picker + swatch. Same two
   *  decisions the export makes, through the same function; the preview is encoded as PNG. */
  const previewBackgroundColor = useMemo(
    () => composeBackgroundColor(
      background,
      resolveBackgroundColor(background, { solidHex: backgroundHex, brandColor: previewBrandColor }),
      "png",
    ),
    [background, backgroundHex, previewBrandColor],
  );
  const catalog = usePhoneCatalog();
  const phoneModels = catalog.models;
  const phoneModelsError = catalog.error;
  const loadPhoneModels = catalog.reload;
  const { styles: caseStyles } = useCaseStyles();
  const [generating, setGenerating] = useState(false);
  /** WS-HF N6 — the cloud job whose Higgsfield submission had an unknown outcome. While set,
   *  Generate is blocked: only the operator can say it is safe to spend again. */
  const [hfStuck, setHfStuck] = useState<{ jobId: string; view: string } | null>(null);
  /** WS-HF N7 — can this device remember a submission across a reload? Probed once. */
  const [hfCanRemember] = useState(storageAvailable);
  const [hfRetryConfirmed, setHfRetryConfirmed] = useState(false);
  const [hfRetriedAfterFailure, setHfRetriedAfterFailure] = useState(false);
  const hfNeedsRetryConfirm = !hfCanRemember && hfRetriedAfterFailure && !hfRetryConfirmed;
  const [dragOver, setDragOver] = useState(false);
  /** Seconds elapsed in the current generate call — drives the progress UI. */
  const [elapsed, setElapsed] = useState(0);
  /** Generated image opened in the full-screen lightbox (click to enlarge). */
  const [lightbox, setLightbox] = useState(false);
  /** Guards against double-clicks landing a second in-flight generate request. */
  const generatingRef = useRef(false);
  /** WS-HF — which case stage `CaseFlow` has on screen, so the stepper can point at it. */
  const [caseStageKey, setCaseStageKey] = useState<"clear" | "magenta" | null>(null);
  /** ED-10 — same guard for SUBMIT. `busy` only disables the button on the next render, so two
   *  fast clicks could each run the create-template path and orphan a template. */
  const submittingRef = useRef(false);
  /** The latest baked mockup payload MaskEditor emitted (emit-only mode — no versionId yet).
   *  Retained so `submit()` can upload it against the freshly-created version. Undefined until
   *  the user previews a mockup or removes the background in the editor below. */
  const bakedPayloadRef = useRef<BakedUploadPayload | null>(null);
  /** F4 — imperative bake handle into the MaskEditor below. `submit()` force-bakes through it
   *  right before the post-submit upload so a slider/calibration change made AFTER the last
   *  emitted bake re-composes with the current params instead of uploading pre-slider pixels. */
  const bakeRef = useRef<MaskBakeHandle>(null);
  /**
   * Pre-submit baked-asset upload status.
   *
   * "uploading" shows an inline progress line on the mask step; "warned" shows a non-blocking
   * warning ON the success screen (the assets landed, but they are PRE-tweak pixels).
   *
   * F-1 (audit, blocking): "blocked" and "failed" are NOT warnings — they STOP the publish. The
   * wizard used to fall through to `submitVersion`, which auto-approves and LOCKS the version, so
   * the template went Active carrying `baked_assets = null`. A locked Active row rejects every
   * later write with a 409 (`cloud/app/catalog/service.py:488-489`), so the operator's whole
   * editor setup was unrecoverable, the batch rendered design-only, and the only signal was one
   * line on a success screen. A bake that could not publish now keeps the version a DRAFT — every
   * setting stays writable and the run is retried in place once hosting works.
   */
  const [assetSync, setAssetSync] = useState<
    "idle" | "uploading" | "warned" | "blocked" | "failed" | "nobake"
  >("idle");
  /** WHY the upload failed, shown verbatim in the `failed` banner — "it didn't sync" named
   *  neither the cause nor the fix, which is exactly how this stayed undiagnosable. */
  const [bakedSyncError, setBakedSyncError] = useState<string | null>(null);
  /**
   * Skip the hosting PRE-FLIGHT for the duration of ONE blocked-banner Retry (mirrors
   * `TemplateEditor`'s `skipHostingPreflightRef`).
   *
   * `hostingEnabled()` is a module value warmed once per session, so an admin who follows the
   * banner's own instruction ("Turn on hosting in Settings → Hosting"), comes back and presses
   * Retry would get the same blocked banner until they signed out and in. Passing `undefined`
   * hands the verdict back to the presign's own 409 — the tested backstop — instead of a stale
   * session value; it never claims hosting is on.
   *
   * Armed ONLY by a Retry from the BLOCKED banner (`retryPublish`), and CONSUMED — read and
   * disarmed together — at the very top of `submit()`, so exactly one attempt ever sees it.
   */
  const skipHostingPreflightRef = useRef(false);
  /** WS-P: who is looking. Decides only whether the hosting-off banner says "Turn on hosting"
   *  or "Ask a platform admin to" — the server is the authority on who may actually do it. */
  const viewerRole = useSessionStore((s) => s.session?.role);
  /**
   * WS-AUTOKEY (owner decision 3) — a CUSTOMER's Mask step is the keyed result, design placement,
   * the test mock-up, Product and Publish. Everything else is not rendered for them; it keeps its default
   * and is still EMITTED on Publish (the bake's layer refs, the render settings), so a hidden
   * setting is never a missing key. Only `"customer"`: an admin — and a host with no session
   * (the standalone embed, the suites) — keeps the full editor.
   */
  const isCustomer = viewerRole === "customer";
  const safeGenerated = useGuideSafeUrl(wiz.generated?.dataUri);
  /** WS-AUTOKEY — the case image is a Higgsfield hand-over (case job or single-stage generate),
   *  so its print area carries shaded magenta: key it automatically, preserve Reference light. A
   *  real photo (manual upload) has no marker and keeps the manual flow and Reference light. */
  const fromHiggsfield = !!wiz.generated;
  /** A Generate run: one template with every view, each keyed in its own editor. */
  const runViews = wiz.generatedViews;
  const runMode = runViews.length > 0;
  /** The view the brand-logo area and the legacy single-view fields belong to. */
  const primaryView = views.includes("back") ? "back" : views[0];
  /** Each run view's automatic key verdict; the ref is what an awaiting Publish reads. */
  const [viewKeys, setViewKeys] = useState<Record<string, RunKey>>({});
  const viewKeysRef = useRef<Record<string, RunKey>>({});
  const keyWaiter = useRef<(() => void) | null>(null);
  /** The run views shown so far: their editors stay mounted. */
  const [shownViews, setShownViews] = useState<string[]>([]);
  /** The one view never shown that Publish is keying now: mounted for its turn only. */
  const [publishView, setPublishView] = useState<string | null>(null);
  /** Each run view's pixel size. A ref: Publish reads the sizes of views it mounts itself. */
  const viewDims = useRef<Record<string, { w: number; h: number }>>({});
  const viewBakeRefs = useRef<Record<string, RefObject<MaskBakeHandle | null>>>({});
  const viewPayloads = useRef<Record<string, BakedUploadPayload>>({});
  const bakeRefFor = (view: string) => (viewBakeRefs.current[view] ??= createRef<MaskBakeHandle>());
  /** The automatic key's verdict for the image on screen; `"pending"` until the editor answers. */
  const [singleKey, setSingleKey] = useState<"pending" | AutoKeyVerdict>("pending");
  /** Whether the case job's stage 2 still has its one retry (read when the key finds nothing). */
  const [stage2RetryLeft, setStage2RetryLeft] = useState(false);
  /** The failed key's named reason (`coverage_check_failed: … 12% …`), for an admin's banner. */
  const [singleKeyReason, setSingleKeyReason] = useState<string | undefined>(undefined);
  const autoKey = runMode ? viewKeys[wiz.activeView]?.verdict ?? "pending" : singleKey;
  const autoKeyReason = runMode ? viewKeys[wiz.activeView]?.reason : singleKeyReason;
  /** The failed key's report to the case job: in flight, lost (offline), or answered. */
  const [keyReport, setKeyReport] = useState<"idle" | "checking" | "failed" | "answered">("idle");
  // A3: an off-coverage key still fitted a print area — a note, not a stop.
  const customerAwaitsKey = isCustomer && fromHiggsfield && (autoKey === "pending" || autoKey === "not-found");
  /** A4: a customer sees the image only once the key has made its print area transparent. */
  const hideFromCustomer = isCustomer && fromHiggsfield && (autoKey === "pending" || autoKey === "not-found");
  const checkNotes = wiz.generated?.checkNotes ?? [];
  const keyUnanswered = () => onAutoMagentaKey("not-found", AUTOKEY_UNREADABLE);
  useEffect(() => {
    if (!hideFromCustomer || autoKey !== "pending") return;
    const t = setTimeout(keyUnanswered, AUTOKEY_TIMEOUT_MS);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-arm per image and per verdict only.
  }, [hideFromCustomer, autoKey, wiz.generated]);
  const imageReference = wiz.generated?.caseJobId ?? wiz.generated?.generationJobId;
  // A new image is a new question.
  useEffect(() => {
    setSingleKey("pending");
    setSingleKeyReason(undefined);
    setKeyReport("idle");
    setStage2RetryLeft(false);
  }, [wiz.generated]);
  /** Tell the image's OWN case job the key failed — never whatever job the store holds by now.
   *  A new image can arrive while the report is in flight; the late answer is then dropped. */
  const reportKey = (reason?: string) => {
    const jobId = wiz.generated?.source === "case-job" ? wiz.generated.caseJobId : undefined;
    if (!jobId) return;
    const forImage = wiz.generated;
    setKeyReport("checking");
    void (async () => {
      try {
        // The server records the failed key, so the stage waits for its retry instead of
        // handing the same image over again (D1).
        const job = reason ? await client.caseJobs.reportCheck(jobId, "magenta", reason)
          : await client.caseJobs.get(jobId);
        if (useWizardStore.getState().generated !== forImage) return;
        setStage2RetryLeft(canRetry(job, "magenta"));
        setKeyReport("answered");
      } catch (e) {
        if (useWizardStore.getState().generated !== forImage) return;
        setKeyReport("failed");
        logger?.log("warn", "Wizard · could not report the failed key to the case job", { error: e, jobId });
      }
    })();
  };
  const recordViewKey = (view: string, verdict: AutoKeyVerdict, reason?: string) => {
    viewKeysRef.current = { ...viewKeysRef.current, [view]: { verdict, reason } };
    setViewKeys(viewKeysRef.current);
    keyWaiter.current?.();
  };
  const onAutoMagentaKey = (verdict: AutoKeyVerdict, reason?: string, view = wiz.activeView) => {
    if (runMode) return recordViewKey(view, verdict, reason);
    setSingleKey(verdict);
    setSingleKeyReason(reason);
    if (verdict !== "keyed") reportKey(reason);
  };
  /** Show a run view; every view shown keeps its editor. */
  const showView = (view: string) => {
    const current = useWizardStore.getState().activeView;
    setShownViews((prev) => [...new Set([...prev, current, view])]);
    wiz.setActiveView(view);
  };
  /** Resolves once `view`'s editor has answered its key. */
  const keyAnswered = (view: string) => new Promise<void>((resolve) => {
    keyWaiter.current = () => { if (viewKeysRef.current[view]) resolve(); };
    keyWaiter.current();
  });
  /**
   * Publish keys every run view first, so no view ships unmasked. A view never shown is mounted
   * for its own turn — keyed, then baked — and the next one only after it: one decode and one key
   * at a time, each with its own deadline. A view left without a bake stays mounted, so the
   * publish gates can still read it. Publish stops on a view whose print area was not found and
   * not drawn by hand, with that view on screen; it is not baked.
   */
  const hasPrintArea = (view: string) => geometryOf(useWizardStore.getState(), view).printArea.length >= 3;
  const keyEveryView = async (): Promise<boolean> => {
    for (const { view } of runViews) {
      if (viewKeysRef.current[view] && viewPayloads.current[view]) continue;
      const own = !shownViews.includes(view) && view !== useWizardStore.getState().activeView;
      if (own) setPublishView(view);
      try {
        await withTimeout(keyAnswered(view), AUTOKEY_TIMEOUT_MS, "Print area key");
      } catch {
        recordViewKey(view, "not-found", AUTOKEY_UNREADABLE);
      } finally {
        keyWaiter.current = null;
      }
      if (own && !viewPayloads.current[view]) {
        if (hasPrintArea(view)) {
          try {
            const bake = bakeRefFor(view).current;
            if (bake) await withTimeout(bake.bakeNow(), AUTOKEY_TIMEOUT_MS, "Print area bake");
          } catch (e) {
            logger?.log("warn", "Wizard · a run view could not bake before publish", { error: e, view });
          }
        }
        if (!viewPayloads.current[view]) setShownViews((prev) => [...prev, view]);
      }
    }
    setPublishView(null);
    const notFound = runViews.find((v) => viewKeysRef.current[v.view]?.verdict === "not-found" && !hasPrintArea(v.view));
    if (notFound) {
      showView(notFound.view);
      return false;
    }
    return true;
  };
  /** Set when the local case-image persist fails on submit: the template still saves, but its
   *  photo didn't land on this device, so we warn (non-blocking) on the success screen. */
  const [caseImageWarned, setCaseImageWarned] = useState(false);
  // The HOSTED case-photo publish failed (or hosting is local-only): other systems' editors
  // will open this template to a blank canvas until an editor Save backfills the photo.
  // Distinct from `caseImageWarned`, which covers the LOCAL persist on this device.
  const [casePublishWarned, setCasePublishWarned] = useState(false);
  /** F-g: the template published with a case photo but NO baked mock-up, because the case image
   *  never decoded on this device so a bake was impossible (ED-4 lets that publish through rather
   *  than stranding the work). Every device would export the design on its own, so the success
   *  screen says so instead of leaving the operator to find out from a blank export. */
  const [noBakeWarned, setNoBakeWarned] = useState(false);
  /** WS-BG F4: "Brand color" was chosen but the workspace's DEFAULT brand has no primary colour
   *  (or the brand list could not be read), so the template publishes with NO background colour
   *  and every export renders it white. The editor's Save says this; the wizard used to swallow
   *  it whole — a bare `catch {}` and no message at all. */
  const [brandColorWarned, setBrandColorWarned] = useState(false);
  /** Platform-global approval gate (same source the TemplateEditor reads). Best-effort +
   *  non-blocking: fetched once on mount to make the submit BUTTON label honest before submit.
   *  The success COPY never trusts this — it derives from the status the server returns. */
  const [approvalsEnabled, setApprovalsEnabled] = useState(false);
  /** The status the LAST submit returned (Active when the gate is off → auto-approved, else
   *  Pending). Drives the success-screen copy so it can never lie about the real state. */
  const [submittedStatus, setSubmittedStatus] = useState<TemplateStatus | null>(null);

  // ED-11 — warn on a browser/app CLOSE while the wizard holds unsaved creation work. The in-app
  // Cancel path already confirms (`confirmDiscardWizard`), but a window close bypassed it entirely
  // and dropped the whole run. Reads the store imperatively so the listener never goes stale.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isWizardDirty(useWizardStore.getState())) return;
      e.preventDefault();
      e.returnValue = ""; // required by Chrome/WebView2 to actually show the prompt
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Tick a 1s timer while a generation is in flight so the progress bar can show
  // elapsed time (the provider returns the image in one call — there is no true %).
  useEffect(() => {
    if (!generating) return;
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [generating]);

  // Read the platform approval gate ONCE (best-effort, non-blocking) so the submit button
  // can say the honest verb — "Submit for approval" when the gate is on, else "Publish".
  // A failure (offline / demo / a client without the method) keeps the default (off); the
  // success copy is still correct regardless because it derives from the submit response.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const policy = await client.approvalPolicy();
        if (!cancelled) setApprovalsEnabled(policy.approvals_enabled);
      } catch {
        /* offline / demo — keep the default (approvals off) */
      }
    })();
    return () => { cancelled = true; };
  }, [client]);

  // Load the Background option group (best-effort). On first load, default the selection to the
  // group's default choice (first choice) when the admin hasn't picked one yet — matching the
  // coordinator's "default = group default" for NEW templates. Never clobbers an existing pick.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resolved = await client.getResolvedOptions();
        if (cancelled) return;
        const group = resolved.groups.find((g) => g.key === "background") ?? null;
        setBackgroundGroup(group);
        if (group && group.choices.length > 0 && !useWizardStore.getState().background) {
          setBackground(group.choices[0].value);
        }
      } catch {
        /* best-effort — no background group means no background write-through */
      }
    })();
    return () => { cancelled = true; };
  }, [client, setBackground]);

  const toggleView = (v: string) =>
    setViews(views.includes(v) ? views.filter((x) => x !== v) : [...views, v]);

  /** Readable message for a failed inline catalog create (403 = not an admin). */
  const createError = (e: unknown, what: string) =>
    setError(e instanceof HttpError && e.status === 403
      ? `Only an admin can add a ${what}.`
      : e instanceof Error ? e.message : String(e));

  /** #5 (extended) — add a phone MODEL inline, mirroring the style flow: the Model
   *  select is no longer a dead end when the device isn't in the catalog yet. */
  const submitNewModel = async (values: Record<string, string>) => {
    chooseModel((await catalog.addModel(values.brand!, values.model!)).id);
  };
  /** Edit the SELECTED model in place (typo fixes without leaving the wizard). */
  const submitEditModel = async (values: Record<string, string>) => {
    catalog.upsert(await client.updatePhoneModel(phoneModelId, { brand: values.brand!, model: values.model! }));
    notifyCatalogChanged("phoneModel");
  };
  const selectedModel = phoneModels.find((p) => p.id === phoneModelId) ?? null;

  /* ── Owner decision 2026-09-24 — a CUSTOMER's Details ─────────────────────────────────────
     The Generate page's four fields — product type, brand, model, SKU — plus the template's
     name, on the customer's locked Mask step. They save through the SAME fields an admin's
     Details save (`createTemplate`'s productType / phoneModelId / code / name), so the
     template round-trips and `match` by phone model finds it. Brand is how the customer
     NARROWS the Model list; the template stores the phone model, which carries its brand. */
  /** The required fields a customer has not filled, in the order the form shows them. */
  const detailsMissing = isCustomer
    ? [
        !category && "Product type",
        !wiz.brand.trim() && "Brand",
        !phoneModelId && "Model",
        !code.trim() && "SKU",
      ].filter((f): f is string => !!f)
    : [];
  const missingSentence = detailsMissing.length === 0 ? null
    : `Fill in ${detailsMissing.length === 1 ? detailsMissing[0]
      : `${detailsMissing.slice(0, -1).join(", ")} and ${detailsMissing[detailsMissing.length - 1]}`} to publish.`;
  const chooseBrand = (brand: string) => {
    wiz.setBrand(brand);
    // A model of ANOTHER brand is no longer a valid choice under this one.
    if (selectedModel && selectedModel.brand.toLowerCase() !== brand.toLowerCase()) setPhoneModelId("");
  };
  const chooseModel = (id: string, known: PhoneModelOption[] = phoneModels) => {
    setPhoneModelId(id);
    const m = known.find((p) => p.id === id);
    if (m && !wiz.brand) wiz.setBrand(m.brand);
    // The name defaults from brand + model until the customer types their own.
    if (m && !wiz.templateNameEdited) wiz.setTemplateName(modelLabel({ brand: m.brand, model: m.model }), false);
  };
  /** An admin's "+ Add new model…" under the chosen brand. */
  const addModel = async (model: string): Promise<boolean> => {
    try {
      const created = await catalog.addModel(wiz.brand, model);
      chooseModel(created.id, [...phoneModels, created]);
      return true;
    } catch (e) {
      createError(e, "phone model");
      return false;
    }
  };
  /** The shared product pickers' state. A customer's brand and model must be ones the catalog knows. */
  const pickerProps = {
    validate: detailsAttempted, categories, category, onCategory: setCategory, brand: wiz.brand, onBrand: chooseBrand, brandOptions: catalog.brands,
    allowAddBrand: !isCustomer, modelId: phoneModelId, onModel: (id: string) => chooseModel(id),
    models: catalog.optionsFor(wiz.brand),
    modelsError: phoneModelsError, onRetryModels: () => void loadPhoneModels(),
    onAddModel: isCustomer ? undefined : addModel,
  };

  const caseImageUrl = useObjectUrl(wiz.references.find((r) => /case/i.test(r.name)) ?? wiz.references[0]);

  /** Add one or more images to the reference / product slot of the generate step. */
  const pickGenerateImage = (slot: "reference" | "product") => async () => {
    setError(null);
    try {
      const files = await io.pickFiles({ accept: IMAGE_ACCEPT, multiple: true, maxBytes: 32 * 1024 * 1024 });
      if (files.length === 0) return;
      if (slot === "reference") wiz.addReferenceImages(files);
      else wiz.addProductImages(files);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /**
   * WS-AUTOKEY — take a Higgsfield image as the case image. The print area and cutouts belong to
   * the PREVIOUS image (a stage-2 retry, a second generate), so they go: the automatic key then
   * detects on this image instead of keeping a quad that was drawn on another one.
   */
  const adoptGenerated = (
    image: GeneratedImage, source: NonNullable<GeneratedImage["source"]>, caseJobId?: string,
  ) => {
    wiz.setGenerated({ ...image, source, ...(caseJobId ? { caseJobId } : {}) });
    wiz.setPrintArea([]);
    wiz.setCutouts([]);
  };

  const generate = async () => {
    // Debounce: a single in-flight request only; ignore repeat clicks.
    if (generatingRef.current) return;
    // WS-HF N6/N7 — a blocked run must not start. `hfStuck` is cleared only by the operator
    // saying they checked their Higgsfield account; `hfNeedsRetryConfirm` by acknowledging
    // that this device cannot protect the retry.
    if (hfStuck || hfNeedsRetryConfirm) return;
    const references = wiz.referenceImages;
    const products = wiz.productImages;
    if (references.length === 0 || products.length === 0 || !prompt.trim() || !selectedModel) return;
    generatingRef.current = true;
    setGenerating(true);
    setError(null);
    setHfStuck(null);
    logger?.log("info", `Wizard · AI generate requested (${references.length} ref + ${products.length} product images)`);
    try {
      // WS-HF: on a desktop connected to Higgsfield, the operator's OWN subscription runs
      // this. The cloud still creates and owns the job (and holds the result), so the only
      // thing that changes is who spends the credits and whose machine makes the call.
      const mcp = higgsfieldMcp();
      // Only a super-admin's own sign-in is ever used; a designer's run is the cloud's.
      const onDevice = mcp.hfMcpAvailable && mayGenerateOnThisComputer();
      const hfStatus = onDevice ? await mcp.hfMcpStatus() : { connected: false };
      if (resolveGenerationSource(onDevice, hfStatus.connected) === "higgsfield-mcp") {
        // "Product images" hold the case design and "Product reference images" the phone photo
        // (the mapping on main). The cloud refuses a job that does not name the phone.
        const [outcome] = await runDeviceGeneration({
          client,
          inputPng: products[0].bytes,
          inputMime: products[0].mime,
          productPng: references[0].bytes,
          productMime: references[0].mime,
          details: { productType: wiz.category, brand: selectedModel?.brand, model: selectedModel?.model },
          views: ["back"],
          mcp,
          downloadBytes: io.downloadBytes?.bind(io),
          logger,
        });
        const url = outcome?.job.resultUrl;
        if (!url) throw new Error("Higgsfield finished but the cloud stored no image.");
        const { bytes, mediaType } = await fetchResultBytes(url);
        // Carry the BYTES, never a signed URL that can expire under the editor.
        adoptGenerated({ dataUri: toDataUri(bytes, mediaType), bytes, mediaType }, "single-stage");
        logger?.log("info", "Wizard · Higgsfield (this device) generate succeeded");
        return;
      }

      // Every uploaded reference + product is sent together in one generation call. This is
      // AI-only: if no provider/activation is configured the cloud returns an error, which we
      // surface (no silent or placeholder fallback, spec 022).
      // The Wizard already knows WHAT is being drawn — the selected phone model and
      // case style — so it says so. These fill `{product}` / `{case_style}` / `{view}`
      // in the generate template, which reached the provider blank before.
      const result = await client.generate(
        references.map((f) => f.bytes),
        products.map((f) => f.bytes),
        prompt,
        {
          product: selectedModel ? `${selectedModel.brand} ${selectedModel.model}`.trim() : undefined,
          productType: wiz.category,
          caseStyle: caseStyles.find((c) => c.id === caseStyleId)?.label,
          view: views.includes("back") ? "back" : views[0],
        },
      );
      const { bytes, mediaType } = decodeDataUri(result.image);
      adoptGenerated({ dataUri: result.image, bytes, mediaType }, "single-stage");
      logger?.log("info", "Wizard · AI generate succeeded");
    } catch (e) {
      setError(generateErrorMessage(e, !isCustomer));
      // WS-HF N6 — an unknown submission cannot be cleared by pressing Generate again: only
      // the operator can find out whether their Higgsfield account was billed.
      if (isStuckOnUnknownSubmission(e) && e.jobId) setHfStuck({ jobId: e.jobId, view: e.view ?? "back" });
      // WS-HF N7 — on a device that cannot remember submissions, the NEXT press is an
      // unprotected retry.
      setHfRetriedAfterFailure(true);
      setHfRetryConfirmed(false);
      logger?.log("error", `Wizard AI generate failed — ${e instanceof Error ? (e.stack ?? e.message) : String(e)}`);
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  /** Download the generated image to disk (data-URI → temporary <a download>). */
  const downloadGenerated = () => {
    if (!wiz.generated || typeof document === "undefined") return;
    const ext = (wiz.generated.mediaType?.split("/")[1] ?? "png").replace("jpeg", "jpg");
    const a = document.createElement("a");
    a.href = wiz.generated.dataUri;
    a.download = `generated-${Date.now()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /** Carry the generated image into the single masking page. */
  const continueToMask = () => {
    if (!wiz.generated) return;
    setError(null);
    wiz.setStep("mask");
  };

  const pick = async () => {
    setError(null);
    try {
      const files = await io.pickFiles({
        accept: IMAGE_ACCEPT,
        multiple: true,
        maxBytes: MAX_REFERENCE_BYTES,
      });
      // IM-5: Escape / Cancel in the OS picker returns []. Advancing anyway put the operator on a
      // blank Mask step with no images, where Auto-detect read `references[0].bytes` and threw a
      // raw TypeError. Same guard `pickGenerateImage` already has.
      if (files.length === 0) return;
      wiz.setReferences(files);
      wiz.setStep("mask");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /**
   * Accept files dropped onto the dropzone. Works on desktop because the Tauri
   * window sets `dragDropEnabled: false`, so the webview receives the native
   * HTML5 drag/drop events (File objects with bytes) — identical to web.
   */
  const ingestDropped = async (list: FileList) => {
    setError(null);
    try {
      // Accept any image: a known image MIME, any `image/*` type, or — when the OS
      // reports no MIME (common for HEIC/AVIF) — an image file extension.
      const isImageFile = (f: File) =>
        IMAGE_ACCEPT.includes(f.type) ||
        f.type.startsWith("image/") ||
        (f.type === "" && /\.(png|jpe?g|webp|gif|bmp|tiff?|heic|heif|avif|svg)$/i.test(f.name));
      const incoming = Array.from(list).filter(isImageFile);
      if (incoming.length === 0) {
        throw new Error("Drop image files (PNG, JPG, WebP, GIF, TIFF, HEIC, …).");
      }
      const picked: PickedFile[] = [];
      for (const f of incoming) {
        if (f.size > MAX_REFERENCE_BYTES) {
          throw new Error(`${f.name} exceeds the 32 MB limit.`);
        }
        picked.push({
          name: f.name,
          bytes: new Uint8Array(await f.arrayBuffer()),
          mime: f.type || undefined,
        });
      }
      wiz.setReferences(picked);
      wiz.setStep("mask");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const onDropReferences = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) void ingestDropped(files);
  };

  const detect = async () => {
    setBusy(true);
    setError(null);
    try {
      const caseRef = wiz.references.find((r) => /case/i.test(r.name)) ?? wiz.references[0];
      // Detection runs 100% LOCALLY on this device — `genz-engine` (native on
      // desktop, WASM on web) performs classical CV print-area detection from the
      // case photo: foreground/background segmentation → largest-region bounding box
      // → camera-cutout heuristic. No cloud call, no OpenAI; nothing leaves the
      // machine. (Passing no proposal selects the engine's pure-CV path.)
      const caseImg: ImageInput = { bytes: caseRef.bytes, role: "case_back", mime: caseRef.mime };
      const detection = await engine.refine(caseImg, undefined);
      // F-5 (a) — THE FALLBACK. On a magenta case photo the detector emits a camera cutout
      // that sits entirely ABOVE the print area (they share only the print area's top edge),
      // which the engine and the cloud both call `cutout_not_subtracted`. Publishing it was
      // refused, and the only signal was an "Invalid" line folded inside a rail section — so
      // the first thing the operator saw was a failed Publish. Repair it here, at the source,
      // so what lands in the editor is valid by construction; then SAY what was repaired.
      const repair = geometry.repairGeometry(detection.printArea, detection.cutouts);
      wiz.setDetection({ ...detection, printArea: repair.printArea, cutouts: repair.cutouts });
      setDetectNotice(detectionNoticeMessage(detection.notes, repair));
      if (repair.changed) {
        logger?.breadcrumb("action", "Auto-detect: repaired invalid geometry before it reached the editor", {
          dropped: repair.dropped, clipped: repair.clipped, clampedPrintArea: repair.clampedPrintArea,
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const drawFromScratch = () => {
    // 002 US2: manual masking when AI is unavailable/low-confidence. Seeds a
    // default print area in place — the MaskEditor below is already shown.
    wiz.setPrintArea([
      { x: 0.15, y: 0.15 },
      { x: 0.85, y: 0.15 },
      { x: 0.85, y: 0.85 },
      { x: 0.15, y: 0.85 },
    ]);
    wiz.setCutouts([]);
  };

  /**
   * Publish one case photo to hosting; its public ref, or undefined (with the success-screen
   * warning) when hosting cannot serve it to other devices.
   */
  const publishCasePhoto = async (templateId: string, caseU8: Uint8Array): Promise<string | undefined> => {
    try {
      // Magic-sniff the real format so the presigned ext/Content-Type match the bytes.
      const ext = caseU8[0] === 0xff && caseU8[1] === 0xd8 ? "jpg"
        : caseU8[0] === 0x52 && caseU8[1] === 0x49 ? "webp"
        : "png";
      const target = await client.templateReferenceUpload(templateId, ext);
      if (target.upload_url.startsWith("file://") || !/^https?:\/\//i.test(target.public_url)) {
        // Local hosting / a root-relative public URL can't serve other devices — skip,
        // and flip the success-screen warning (a warn log alone hid this).
        logger?.log("warn", "Wizard · hosting can't publish the case photo for other devices (local-only target or non-durable public URL)", {
          templateId, step: "caseImagePublish",
        });
        setCasePublishWarned(true);
        return undefined;
      }
      const mime = ext === "jpg" ? "image/jpeg" : ext === "webp" ? "image/webp" : "image/png";
      const res = await fetchWithTimeout(target.upload_url, {
        method: "PUT", headers: { "Content-Type": mime }, body: caseU8 as BodyInit,
      }, UPLOAD_TIMEOUT_MS, "Case photo publish");
      if (!res.ok) throw new Error(`case-photo publish PUT -> ${res.status}`);
      logger?.breadcrumb("action", "Wizard · case photo published (cross-device editor fix)", {
        templateId, bytes: caseU8.length, ext,
      });
      return target.public_url;
    } catch (pubErr) {
      logger?.log("warn", "Wizard · case-photo publish FAILED — other systems' editors show a blank canvas until an editor Save backfills it", {
        error: pubErr, step: "caseImagePublish", templateId,
      });
      setCasePublishWarned(true);
      return undefined;
    }
  };

  /** The Generate index's hand-over step for every job in this template: "In template" once a
   *  version holds them, "Published" after. A failure to record it never blocks the publish. */
  const recordGenerations = async (step: "in_template" | "published", templateId?: string) => {
    for (const jobId of generationJobIds(wiz)) {
      try {
        onGenerationPublished?.(await client.recordGenerationProgress(jobId, step, templateId));
      } catch (e) {
        logger?.log("warn", `Wizard · the generation could not be marked ${step}`, { error: e, jobId, templateId });
      }
    }
  };
  const submit = async () => {
    // F-f: CONSUME the hosting-pre-flight hatch here, before anything can return — read it once
    // into a local and disarm the ref in the same breath.
    //
    // It used to be read where it is used, deep inside the try, and cleared in that try's
    // `finally`. So a Retry that landed on the re-entrancy guard below returned without ever
    // reaching either, leaving the latch armed for every later Publish in the run — silently
    // disarming the pre-flight the banner exists to enforce. Clearing it AT the guard instead
    // just moves the bug: both clicks of a double-click run in one tick, so the second one's
    // clear would cancel the hatch out from under the attempt that is actually in flight.
    // Consuming it up here gives each attempt exactly the value armed for it, and leaves nothing
    // behind for the next one.
    const skipHostingPreflight = skipHostingPreflightRef.current;
    skipHostingPreflightRef.current = false;
    // ED-10: re-entrancy guard. The button only disables once `busy` has RENDERED, so a
    // double-click (or Enter held) could land two submits — each running its own
    // createTemplate/createVersion pair and orphaning a template.
    if (submittingRef.current) return;
    if (customerAwaitsKey) return;
    if (detailsMissing.length > 0) {
      setDetailsAttempted(true);
      // Focus after validation has rendered the field's explanation.
      requestAnimationFrame(() => detailsRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
      return;
    }
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    setAssetSync("idle");
    setBakedSyncError(null);
    setCaseImageWarned(false);
    setCasePublishWarned(false);
    setNoBakeWarned(false);
    setBrandColorWarned(false);
    setSubmittedStatus(null);
    // "Make template" from a mock-up: with no print area drawn it publishes marked, with no
    // geometry to check and no mock-up to bake (nothing can be placed on it).
    const mockupOnly = wiz.mockupOnly && wiz.printArea.length === 0;
    try {
      if (runMode && !(await keyEveryView())) return;
      for (const view of runMode ? runViews.map((rv) => rv.view) : [null]) {
        const g = view ? geometryOf(useWizardStore.getState(), view) : wiz;
        const v = mockupOnly ? null : await engine.geometryValidate(g.printArea, g.cutouts);
        if (v && !v.valid) {
          if (view) showView(view);
          setError(`Cannot submit invalid geometry${view ? ` (${viewLabel(view)})` : ""}: ${v.errors.join(", ")}`);
          return;
        }
      }

      // Create the template + its first Draft version on first submit (the flow
      // never created one before — this is the bug being fixed). Subsequent
      // submits of the same wizard run reuse the carried template/version.
      let templateId = wiz.templateId;
      let versionId = wiz.versionId;
      let versionNumber = wiz.versionNumber;
      let versionToken = wiz.versionToken;
      // ED-10: create the TEMPLATE and its VERSION in two independently-resumable steps.
      //
      // These used to share one `if`, and `templateId` was only written to the store by the
      // `setVersion` call at the END. So a failure in createVersion / patchRenderSettings left the
      // store with NO template id: the next retry re-entered the block and called createTemplate
      // AGAIN, orphaning an empty template every time — and once a Code was filled in, that second
      // create 409'd ("code already in use") forever, leaving the run unrecoverable. Now the id is
      // persisted the instant it exists, and the retry reuses the template it already created.
      if (!templateId) {
        // P3: persist the chosen catalog metadata (Category/Model/Style) on create.
        const created = await client.createTemplate({
          name: wiz.templateName.trim()
            || (selectedModel ? modelLabel(selectedModel) : defaultTemplateName(wiz.references)),
          productType: category,
          phoneModelId: phoneModelId || undefined,
          caseStyleId: caseStyleId || undefined,
          baseType: baseType || undefined,
          code: code.trim() || undefined,
        });
        templateId = created.templateId;
        wiz.setTemplateId(templateId); // IMMEDIATELY — before anything else can fail
      }
      if (!versionId || versionNumber === undefined || !versionToken) {
        // `createVersion` REUSES an open draft, so a retry after a mid-flight failure lands on the
        // same version rather than stacking new ones.
        const draft = await client.createVersion(templateId);
        versionId = draft.versionId;
        versionNumber = draft.versionNumber;
        versionToken = draft.versionToken;
        wiz.setVersion(templateId, versionId, versionNumber, versionToken);
        await recordGenerations("in_template");
      }
      // P3 + Task 6: PATCH the draft render_settings with the chosen output format and/or
      // per-template background. This bumps the version_token, so re-read it before submit
      // (avoids a 409). Only patch when there's something to write (compat: no background
      // chosen → nothing sent → render_settings.background stays absent).
      //
      // P2-4: this runs on EVERY submit, not only the one that created the version. It used to
      // live INSIDE the `if (!versionId …)` block, so after any first-submit failure (or after the
      // operator went Back, changed the format/background and resubmitted) the retry reused the
      // existing draft and skipped the patch entirely — the chosen output format and background
      // were silently dropped, and the template published with the schema defaults. Re-patching an
      // unchanged value is a harmless no-op write.
      const renderPatch: {
        formats?: string[]; background?: string;
        background_color?: [number, number, number, number] | null;
      } = {};
      if (outputFormat) renderPatch.formats = [outputFormat];
      if (background) {
        renderPatch.background = background;
        // WS-BG: resolve the COLOUR the choice names, in the same patch (the EMIT leg). The
        // brand list is read HERE, at publish, not at render: a locked version renders on other
        // devices from its own payload. An unreadable list or a brand with no colour leaves the
        // colour null — the template renders white rather than on an invented swatch.
        let brandColor: string | null = null;
        if (background === "brand_color") {
          try {
            brandColor = brandColorPrimary(await client.listBrands());
          } catch (e) {
            // F4: a bare `catch {}` here meant the operator published a Brand-color template
            // that renders WHITE and was told nothing, anywhere — not on screen, not in the log.
            // The publish still proceeds (an unresolvable colour is not a reason to block the
            // work), but it is now both recorded and said out loud on the success screen.
            logger?.log("warn", "Publish: couldn't read the brand list — the Brand color background stays unresolved", {
              error: e, step: "backgroundBrandColor", templateId,
            });
          }
          if (!brandColor) setBrandColorWarned(true);
        }
        renderPatch.background_color = resolveBackgroundColor(background, {
          solidHex: backgroundHex, brandColor,
        });
      }
      if (Object.keys(renderPatch).length > 0) {
        await client.patchRenderSettings(templateId, renderPatch);
        const refreshed = await client.createVersion(templateId); // reuses the draft, fresh token
        versionId = refreshed.versionId;
        versionNumber = refreshed.versionNumber;
        versionToken = refreshed.versionToken;
        wiz.setVersion(templateId, versionId, versionNumber, versionToken);
      }

      // The case photo(s), selected ONCE for the whole submit: each is both the thing published to
      // hosting further down AND the thing that decides whether a missing bake is a refusal.
      // A template with no case photo declares no product, so it is a true classic template and
      // must keep publishing exactly as it does today. A mock-up only run has a photo but nothing
      // to bake, so for the bake it counts as having none. A Generate run authors every view, each
      // with its own photo, editor and bake; any other run authors the one photo on `primaryView`.
      const caseBytesRaw = wiz.generated?.bytes
        ?? (wiz.references.find((r) => /case/i.test(r.name)) ?? wiz.references[0])?.bytes;
      const targets: PublishTarget[] = runMode
        ? runViews.map((rv) => ({
            view: rv.view, bytes: rv.image.bytes, bake: bakeRefFor(rv.view),
            payload: () => viewPayloads.current[rv.view] ?? null,
          }))
        : [{
            view: primaryView ?? "back",
            bytes: caseBytesRaw
              ? (caseBytesRaw instanceof Uint8Array ? caseBytesRaw : new Uint8Array(caseBytesRaw as ArrayBuffer))
              : null,
            bake: bakeRef, payload: () => bakedPayloadRef.current,
          }];
      const hasCasePhoto = (t: PublishTarget) => !!t.bytes && t.bytes.length > 4 && !mockupOnly;
      // Cross-device baked-asset upload (spec 019) — run BEFORE submit, against the DRAFT.
      // F6 root cause: publishing with the approval gate off AUTO-APPROVES on submit, which LOCKS
      // the version (router submit_version → approve_version), and `set_baked_assets` rejects any
      // write to a locked version with 409 (service.py: `if v.locked: raise CatalogError("locked")`).
      // So uploading AFTER submit was guaranteed a 409 → the "assets didn't sync" banner on EVERY
      // publish. TemplateEditor never hit this because it syncs while the version is still a Draft
      // (before Save/submit) and adopts the rotated token; the wizard now mirrors that ordering.
      // Set when a force-bake failed/no-emitted while dirty: the payload uploaded below is
      // PRE-tweak pixels, so even a clean upload must end "warned", not "idle".
      let staleBake = false;
      // F-g: the THIRD door, held open on purpose but never again in silence. When the case image
      // never decoded, the refusal below stands down (ED-4) and this publish goes ahead with a
      // case photo and no bake — the audited shape. It says so on the success screen.
      let publishedWithoutBake = false;
      for (const t of targets) {
        // F4 — force-bake any pending mask/calibration change made AFTER the last emitted bake. The
        // MaskEditor bakes in emit-only mode: a slider tweak re-composes LIVE and never re-emits, so
        // the retained payload would hold pre-slider pixels. `bakeNow()`'s onBaked emit is
        // SYNCHRONOUS, so the payload is fresh the instant the await resolves. Never blocks the save.
        try {
          if (t.bake.current?.isDirty()) {
            logger?.breadcrumb("action", "Wizard · baking pending mask changes before upload", {
              templateId, versionNumber, view: t.view,
            });
            if (!(await t.bake.current.bakeNow())) {
              logger?.log(
                "warn",
                "Wizard · force-bake produced no emit while dirty — uploading the last retained payload (may be stale)",
                { templateId, versionNumber, view: t.view },
              );
              staleBake = true;
            }
          }
        } catch (bakeErr) {
          logger?.log("warn", "Wizard · force-bake threw — uploading retained payload", {
            error: bakeErr, templateId, versionNumber, view: t.view,
          });
          staleBake = true;
        }
        // F-c: LAST-CHANCE BAKE, then a hard stop. When the force-bake above threw or emitted
        // nothing there is no payload, and publishing would lock a version carrying a case photo and
        // `baked_assets = null`. The bake is attempted once more first (mirroring
        // `TemplateEditor.saveDraft`), which is also what makes the banner's Retry meaningful.
        if (hasCasePhoto(t) && !t.payload() && t.bake.current) {
          try {
            await t.bake.current.bakeNow();
          } catch (lastErr) {
            logger?.log("warn", "Wizard · last-chance bake threw before publish", {
              error: lastErr, templateId, versionNumber, view: t.view,
            });
          }
        }
        // TWO gates, both copied from `TemplateEditor.saveDraft`: with no mask editor mounted there
        // is no way to ASSESS bake state; and when the case image never decoded (ED-4) a bake is
        // PHYSICALLY impossible, so refusing would strand the operator — the wizard warns instead.
        if (hasCasePhoto(t) && !t.payload() && t.bake.current?.sourceReady()) {
          logger?.log(
            "error",
            "Wizard · no baked mock-up could be prepared — publish REFUSED, the version stays a Draft",
            { templateId, versionNumber, view: t.view },
          );
          if (runMode) showView(t.view);
          setBakedSyncError(null);
          setAssetSync("nobake");
          return; // `finally` clears busy + the re-entrancy guard
        }
        if (hasCasePhoto(t) && !t.payload()) {
          logger?.log(
            "warn",
            "Wizard · publishing a template with a case photo but NO baked mock-up (the case image never decoded here)",
            { templateId, versionNumber, view: t.view },
          );
          publishedWithoutBake = true;
        }
      }
      setNoBakeWarned(publishedWithoutBake);
      // Upload the retained baked assets against the still-unlocked DRAFT so a second device
      // hydrates the FULL layered render. `setBakedAssets` rotates the draft's version_token, so we
      // ADOPT the returned token before submit — otherwise submitVersion would present the
      // pre-rotation token and 409 "changed elsewhere". A run merges each view's refs into one
      // per-view map (the back's on the primary fields), so every upload persists them all.
      // F-1 (audit, blocking): a failure here STOPS the publish. It used to fall through to
      // `submitVersion`, which auto-approves and LOCKS the version with `baked_assets = null`. The
      // draft token is unchanged on failure, so the SAME run is retried in place.
      const baked = targets.filter((t) => t.payload());
      if (baked.length > 0) {
        setAssetSync("uploading");
        // Start-of-upload breadcrumb so the activity trail shows the upload BEGAN even when it
        // later fails mid-flight.
        logger?.breadcrumb("action", "Wizard · uploading baked assets before submit", {
          templateId, versionNumber,
        });
        let bakedRefs: BakedAssetRefsMap | null = null;
        try {
          for (const t of baked) {
            // ED-18: bounded — a wedged upload used to hold "Uploading template assets…" forever.
            const synced = await withTimeout(
              syncBakedAssetsToCloud(client, logger, {
                templateId,
                versionNumber,
                payload: t.payload()!,
                surface: "wizard",
                mapRefs: runMode ? (refs) => {
                  bakedRefs = mergeViewRefs(bakedRefs, t.view, refs);
                  return bakedRefs as unknown as Record<string, unknown>;
                } : undefined,
                // F-1: the WS-P pre-flight. A Retry hands the verdict back to the presign's 409
                // (see `skipHostingPreflightRef`).
                hostingEnabled: skipHostingPreflight ? undefined : hostingEnabled(),
              }),
              ASSET_SYNC_TIMEOUT_MS,
              "Template asset upload",
            );
            versionToken = synced.newVersionToken;
            wiz.setVersion(templateId, versionId, versionNumber, versionToken);
          }
          // A clean upload of a STALE payload is still a warn — other devices will render
          // pre-tweak pixels (the force-bake above failed to refresh them).
          setAssetSync(staleBake ? "warned" : "idle");
          setBakedSyncError(null);
        } catch (syncErr) {
          logger?.log(
            "error",
            "Wizard · baked-asset sync FAILED — publish REFUSED, the version stays a Draft",
            { error: syncErr, templateId, versionNumber },
          );
          setBakedSyncError(syncErr instanceof Error ? syncErr.message : String(syncErr));
          // An upload that CANNOT succeed is a different offer from one that might: hosting is
          // off for the whole workspace. Same split as `TemplateEditor` (`blocked` vs `failed`).
          setAssetSync(syncErr instanceof HostingDisabledError ? "blocked" : "failed");
          // HARD STOP — do NOT publish. The draft keeps the template id, the version, the
          // patched render settings and (in the wizard store) every mask, view and image.
          return; // `finally` clears busy + the re-entrancy guard
        }
      }

      // Publish each case image to hosting and persist its ref with its view. Without this the
      // case photo lived ONLY in this device's local store and `base_case_image_ref` was never set
      // — so every OTHER system/user opened the template to a BLANK editor canvas. Best-effort.
      const caseRefs: Record<string, string> = {};
      for (const t of targets) {
        const ref = t.bytes && t.bytes.length > 4 ? await publishCasePhoto(templateId, t.bytes) : undefined;
        if (ref) caseRefs[t.view] = ref;
      }

      // Persist the corrected masks server-side (Draft → Pending, then auto-approve → Active when
      // the gate is off). This LOCKS the version, so it MUST come after the baked-asset upload
      // above. P3: when the user selected views, persist that exact view set; else the cloud
      // applies the single primary view as before, with the hosted case-photo ref forced onto it.
      // ED-16: a single photo authors exactly ONE view, `primaryView`. Copying its print area onto
      // EVERY selected view stamped geometry that matches none of them, so the extra views are
      // created EMPTY instead and are masked in the template editor. A run authors every view.
      const geometry = (view: string) => (runMode ? geometryOf(useWizardStore.getState(), view) : wiz);
      const primaryGeometry = geometry(targets[0].view);
      const wireMasks = toWireMasks(primaryGeometry.printArea, primaryGeometry.cutouts);
      const caseImageRef = caseRefs[targets[0].view];
      // P0-2 (DATA LOSS): the brand-logo quad must ride on the CHOSEN VIEW's dict — when `views` is
      // present the cloud ignores the separate `brand_logo_placement` argument. It goes on
      // `primaryView`, matching the masks it belongs to.
      const brandPlacement = brandArea
        ? brandArea.map((p) => [p.x, p.y] as [number, number])
        : null;
      // WS-EXIF: the masks go out with the pixel size of the photo they were drawn on. Without it
      // a wizard-created template is a legacy record from birth.
      const frameOf = (view: string) => {
        const dims = runMode ? viewDims.current[view] : baseDims;
        return dims ? ([dims.w, dims.h] as [number, number]) : null;
      };
      const authored = (view: string) => runMode || view === primaryView;
      const chosenViews = views.length > 0
        ? views.map((vt) => {
            const g = geometry(vt);
            const frame = frameOf(vt);
            return {
              view_type: vt, masks: authored(vt) ? toWireMasks(g.printArea, g.cutouts) : [], layers: [],
              ...(mockupOnly ? { mockup_only: true } : {}),
              ...(brandPlacement && vt === primaryView ? { brand_logo_placement: brandPlacement } : {}),
              ...(caseRefs[vt] && authored(vt) ? { base_case_image_ref: caseRefs[vt] } : {}),
              ...(frame && authored(vt) ? { base_image_size: frame } : {}),
            };
          })
        : (caseImageRef
            ? [{
                view_type: "back", masks: wireMasks, layers: [],
                base_case_image_ref: caseImageRef,
                ...(brandPlacement ? { brand_logo_placement: brandPlacement } : {}),
                ...(frameOf("back") ? { base_image_size: frameOf("back") } : {}),
              }]
            : null);
      // ED-18: bounded — a hung submit left the button on "Saving…" with no recourse. The timeout
      // surfaces through `submitErrorMessage` like any other failure, and the retry is safe:
      // `templateId`/`versionId` are already in the store, so it reuses them (see ED-10 above).
      const submitted = await withTimeout(
        client.submitVersion(
          templateId,
          versionNumber,
          versionToken,
          wireMasks,
          // Legacy single-view channel — kept in step with the per-view dict above (the cloud
          // prefers the `views` branch; see P0-2).
          brandPlacement,
          chosenViews,
        ),
        SUBMIT_TIMEOUT_MS,
        "Template submit",
      );
      wiz.setVersion(templateId, submitted.versionId, submitted.versionNumber, submitted.versionToken);
      // WS-AUTOKEY — the case job is finished HERE, when its image became a template. Stage 2's
      // Approve only handed the image over, so the stage-2 retry stayed available while the
      // automatic key had not yet found the magenta (a `done` job refuses `retry`). Read first
      // and approve ONLY stage 2 under review: an approve on stage 1 queues a PAID stage 2.
      // Best-effort — the template is already submitted, and a job left under review only means
      // the same photos resume it rather than paying again.
      // The job stored ON the image at the hand-over — not the store's `caseJobId`, which a
      // resumed or restored run can have moved on (fix round 2, item 4).
      const finishJobId = wiz.generated?.source === "case-job" ? wiz.generated.caseJobId : undefined;
      if (finishJobId) {
        try {
          const job = await client.caseJobs.get(finishJobId);
          if (canApprove(job, "magenta")) await client.caseJobs.approve(finishJobId);
        } catch (jobErr) {
          logger?.log("warn", "Wizard · the case job could not be finished after publish — it stays under review", {
            error: jobErr, jobId: finishJobId, templateId,
          });
        }
      }
      // The Generate index shows each generation as published, with the template it made.
      await recordGenerations("published", templateId);
      // Success copy is driven by the REAL returned status (Active when the gate is off →
      // auto-approved; Pending when it's on) — never by the local approvals guess.
      setSubmittedStatus(submitted.status);

      // Persist the case image (generated, else the case reference) LOCALLY so the
      // template keeps its image — encrypted on disk (desktop) / IndexedDB (web) —
      // decoupled from the cloud. Best-effort: never blocks the save. (`caseU8` was
      // selected above, where the same bytes were published to hosting.)
      const caseU8 = targets[0].bytes;
      if (caseU8) {
        // AWAIT the local persist (was fire-and-forget) so a failure is caught + surfaced.
        // It must NOT fail the submit (the template is already saved) — a swallowed reject
        // used to silently lose the local photo with no signal; now we log richly and warn
        // non-blockingly on the success screen so the admin can reopen to re-attach it.
        try {
          await storeTemplateImage(engine, templateId, caseU8);
        } catch (imgErr) {
          logger?.log(
            "error",
            "Wizard · local case-image persist FAILED after save (the template's photo won't show until re-attached)",
            { error: imgErr, templateId },
          );
          setCaseImageWarned(true);
        }
      }

      wiz.setStep("submit");
    } catch (e) {
      setError(submitErrorMessage(e));
      if (e instanceof HttpError && e.status === 409
        && errorDetail(e) === "template code already in use") {
        const suggestion = (e.body as { suggested_code?: unknown } | undefined)?.suggested_code;
        setCodeRecovery({
          suggestion: typeof suggestion === "string" && suggestion.length <= 60 ? suggestion : undefined,
          changed: false,
        });
        codeInputRef.current?.focus();
      }
      logger?.log("error", "Wizard · template publish failed", {
        error: e, templateId: useWizardStore.getState().templateId,
        jobId: wiz.generated?.generationJobId ?? wiz.generated?.caseJobId,
      });
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  /**
   * F-1: re-run the refused publish from the banner. The draft, its render settings and every
   * mask/view in the wizard store are still here, so this is the same run, not a new template.
   * `skipHostingPreflightRef` lets an admin who has just turned hosting on get past the value
   * this session warmed at sign-in.
   */
  const retryPublish = () => {
    // F-e: only a HOSTING-OFF refusal has a stale session value to get past. Opening the hatch
    // after an ordinary upload failure would suppress a pre-flight that is telling the truth,
    // and send the whole bake to a presign that is going to 409 anyway. Mirrors
    // `TemplateEditor.retryBakedSync`.
    skipHostingPreflightRef.current = assetSync === "blocked";
    void submit();
  };

  /**
   * F-d — discard the run, and take its ORPHAN with it.
   *
   * A refused publish leaves a Draft that `createTemplate` already made, carrying the operator's
   * Code. `confirmDiscardWizard` then resets the store, so the id is gone and nothing will ever
   * reuse that Draft — but the Code is still taken, and the NEXT run that types it 409s on
   * `createTemplate` forever (the failure `submit()`'s ED-10 comment describes).
   *
   * TWO steps, in this order, because archiving alone is NOT enough: `archive_template` is a SOFT
   * delete (`archived = True`) and `code_in_use` (`cloud/app/catalog/codes.py`) counts archived
   * rows, so the Code would stay reserved by a template nobody can see any more — worse than
   * leaving it visible. Verified against the live API: creating a template with an archived
   * template's code still answers 409 "template code already in use".
   *
   *   1. PARK the Code — patch it to a unique `<code>-discarded-<timestamp>` so the operator's
   *      own Code is genuinely free again (the uniqueness check reads the live column, and
   *      `exclude_id` makes patching a row's own code a no-conflict).
   *   2. ARCHIVE the Draft, so the half-made template leaves the catalog.
   *
   * F-h — TWO gates before anything is written, because this cleanup can destroy real work.
   *
   * `step !== "submit"` is NOT enough on its own. `submitVersion` is wrapped in
   * `withTimeout(SUBMIT_TIMEOUT_MS)`, so a submit the SERVER COMMITTED can still reject on the
   * wire: the wizard stays on the mask step holding a template that is now Active, and this
   * function would park its Code and archive it — deleting the operator's published template as
   * "cleanup". So:
   *
   *   Gate 1, local: only the REFUSAL states. `blocked` / `failed` / `nobake` are the states
   *   `submit()` sets on the paths that return BEFORE `submitVersion`, so they are positive
   *   evidence that nothing was published. Any other state — including a submit that threw —
   *   leaves the template alone; an orphan is cheap, a deleted Active template is not.
   *
   *   Gate 2, server: re-read the template and skip unless every version we can see is a Draft.
   *   Gate 1 reads this device's belief; this reads the system of record. A fetch that fails is
   *   also a skip — not knowing is not permission.
   *
   * Best-effort after that: neither write may trap the operator in a wizard they asked to leave,
   * and step 2 runs even if step 1 fails.
   */
  const discardRun = () => {
    // Read BEFORE `confirmDiscardWizard`, which resets the store.
    const refused = assetSync === "blocked" || assetSync === "failed" || assetSync === "nobake";
    const orphanId = refused && wiz.step !== "submit" ? wiz.templateId : undefined;
    if (!confirmDiscardWizard()) return; // stay put; the store is untouched
    if (orphanId) {
      void (async () => {
        // Gate 2 — the system of record decides. `currentVersion` is the HEAD version and
        // `headVersion` is the newest when the server sends both; either being past Draft means
        // this template carries published work.
        let live: ProductTemplate;
        try {
          live = await client.getTemplate(orphanId);
        } catch (e) {
          logger?.log("warn", "Wizard · could not re-read the discarded template — leaving it untouched", {
            error: e, templateId: orphanId,
          });
          return;
        }
        const statuses = [live.currentVersion?.status, live.headVersion?.status].filter(Boolean);
        if (statuses.some((st) => st !== "Draft")) {
          logger?.log(
            "warn",
            "Wizard · the discarded run's template already carries a published version — not archiving it",
            { templateId: orphanId, statuses },
          );
          return;
        }
        logger?.breadcrumb("action", "Wizard · discarding the run — releasing the Code and archiving the Draft", {
          templateId: orphanId,
        });
        try {
          await client.updateTemplateDetails(orphanId, {
            code: `discarded-${orphanId.slice(0, 8)}-${Date.now()}`,
          });
        } catch (e) {
          logger?.log("warn", "Wizard · could not release the discarded Draft's Code — reusing it will 409", {
            error: e, templateId: orphanId,
          });
        }
        try {
          await client.archiveTemplate(orphanId);
        } catch (e) {
          logger?.log("warn", "Wizard · could not archive the discarded Draft — it stays in the catalog", {
            error: e, templateId: orphanId,
          });
        }
      })();
    }
    onClose?.();
  };

  /* The stepper.
     WS-HF: the two-stage flow adds "Case photos" as the entry and gives each AI stage its own
     slot, because they are two decisions the operator makes with credits between them. The
     legacy single-stage generate and the manual upload SHARE the first slot — they are other
     ways through the same first step, not extra steps. */
  const fromGeneratePage = wiz.origin === "generate";
  /** Owner 2026-10-02: a Generate-made template shows the preview and Product; an admin keeps
   *  every other tool, folded into one "Advanced (admin)" section. */
  const railMode = isCustomer ? "placement" : fromGeneratePage ? "basic" : "advanced";
  const brandLogoToggle = (
    <label className="field--check wizard__brand-logo" data-testid="add-brand-logo">
      <input
        type="checkbox"
        checked={brandArea !== null}
        onChange={(e) => setBrandArea(e.target.checked
          ? defaultBrandRect(runMode && primaryView ? viewDims.current[primaryView] ?? null : baseDims) : null)}
      />
      <span>
        Add a brand-logo area — drag the amber box to position where an approved
        brand’s logo is merged on Export. (Defaults to a 200×100px box in the centre.)
      </span>
    </label>
  );
  const brandLogoRail = railMode === "basic"
    ? [{ key: "brand-logo", head: "Brand-logo area", body: brandLogoToggle }] : undefined;
  /** A run view's editor: the single image's settings, with the view's own image, geometry, key
   *  and bake. The brand-logo area belongs to the primary view. */
  const runEditorProps = (rv: RunView): Omit<MaskEditorProps, "caseImageUrl"> => {
    const g = geometryOf(wiz, rv.view);
    const primary = rv.view === primaryView;
    return {
      engine, logger, baseType, railMode, view: rv.view,
      printArea: g.printArea,
      cutouts: g.cutouts,
      onPrintAreaChange: (printArea) => wiz.setViewGeometry(rv.view, { printArea }),
      onCutoutsChange: (cutouts) => wiz.setViewGeometry(rv.view, { cutouts }),
      brandArea: primary ? brandArea : null,
      onBrandAreaChange: primary ? setBrandArea : undefined,
      onNaturalDims: (d) => { viewDims.current[rv.view] = d; },
      bakeRef: bakeRefFor(rv.view),
      templateBackgroundColor: previewBackgroundColor,
      onBaked: (payload) => { viewPayloads.current[rv.view] = payload; },
      autoMagentaKey: true,
      shadedMagentaGuide: true,
      initialPrintLight: "reference",
      onAutoMagentaKey: (verdict, reason) => onAutoMagentaKey(verdict, reason, rv.view),
      onCaseImageError: isCustomer ? () => onAutoMagentaKey("not-found", AUTOKEY_UNREADABLE, rv.view) : undefined,
      coverageHeightPct: rv.image.productHeightPct,
      clearCaseImage: rv.clear,
      adminRailItems: primary ? brandLogoRail : undefined,
    };
  };
  const steps = fromGeneratePage ? GENERATE_PAGE_STEPS : CASE_STEPS;
  const stepperKey =
    wiz.step === "generate" || wiz.step === "upload" ? (fromGeneratePage ? "generate" : "case")
      : wiz.step === "stages" ? (caseStageKey ?? "magenta")
        : wiz.step;
  const currentIdx = steps.findIndex((s) => s.key === stepperKey);

  const codeRecoveryControls = codeRecovery && (
    <div className="error-recovery" role="status" data-testid="wizard-code-recovery">
      <span className="error-recovery__steps">
        {codeRecovery.changed ? "1. Code updated. 2. Retry saving your template."
          : "1. Choose a new code. 2. Retry saving your template."}
      </span>
      {!codeRecovery.changed && codeRecovery.suggestion && (
        <button type="button" className="btn btn--sm" disabled={busy}
          onClick={() => setCode(codeRecovery.suggestion!)} data-testid="wizard-use-suggested-code">
          Use {codeRecovery.suggestion}
        </button>
      )}
      <button type="button" className="btn btn--sm btn--ghost" onClick={() => codeInputRef.current?.focus()}
        data-testid="wizard-edit-code">Edit code</button>
      {codeRecovery.changed && (
        <button type="button" className="btn btn--sm" disabled={busy || customerAwaitsKey || detailsMissing.length > 0}
          onClick={() => void submit()} data-testid="wizard-retry-save">
          {busy ? "Saving…" : "Retry save"}
        </button>
      )}
    </div>
  );

  return (
    <section className="wizard" data-testid="wizard">
      <div className="panel-head">
        <div>
          <p className="panel-sub">Reference photos in, a human-locked, mock-up-ready template out.</p>
        </div>
        {onClose && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={discardRun}
            // A Cancel mid-submit would reset the store under a request still writing to the
            // template this run created.
            disabled={busy}
            data-testid="wizard-cancel"
          >
            Cancel
          </button>
        )}
      </div>

      <ol className="stepper" aria-label="Template creation steps">
        {steps.map((s, i) => {
          const state = i < currentIdx ? "done" : i === currentIdx ? "active" : "todo";
          return (
            <li key={s.key} className="is-contents">
              <span className={`stepper__step stepper__step--${state}`}>
                <span className="stepper__num">{state === "done" ? <Icon name="check" size={16}/> : i + 1}</span>
                {s.label}
              </span>
              {i < steps.length - 1 && <span className="stepper__sep" aria-hidden="true" />}
            </li>
          );
        })}
      </ol>

      {error && <p className="banner banner--danger" role="alert" data-testid="wizard-error">
        <ErrorNotice detail={error} reference={imageReference} />
      </p>}

      {/* WS-HF N6 — the ONLY way out of an unknown Higgsfield submission. Pressing Generate
          again cannot clear it, by design; the label says exactly what is being confirmed. */}
      {hfStuck && (
        <p className="banner banner--danger" data-testid="wizard-hf-stuck">
          <button type="button" className="btn btn--sm" data-testid="wizard-hf-clear-stuck"
            onClick={() => void (async () => {
              const design = wiz.productImages[0];
              if (design) await clearStuckDeviceRun(hfStuck.jobId, design.bytes, hfStuck.view);
              setHfStuck(null);
              setError(null);
            })()}>
            I checked my Higgsfield account — clear and allow a new run
          </button>
        </p>
      )}

      {/* WS-HF N7 — this device cannot remember a submission across a reload, so a retry is
          NOT protected from billing twice. Said plainly, and acknowledged before the press. */}
      {hfNeedsRetryConfirm && (
        <p className="banner banner--danger" data-testid="wizard-hf-retry-unprotected">
          This computer cannot remember submissions, so a retry may be billed twice.
          <button type="button" className="btn btn--sm" data-testid="wizard-hf-confirm-retry"
            onClick={() => setHfRetryConfirmed(true)}>
            Generate anyway
          </button>
        </p>
      )}

      <div className="wizard__body card">
        {/* WS-HF — the two-stage flow owns the first three screens. `setGenerated` is the SAME
            hand-over the single-stage path uses, so mask → submit continue unchanged: the
            approved magenta image becomes the case image the detect/refine path masks. */}
        {(wiz.step === "case" || wiz.step === "stages") && (
          <CaseFlow
            product={selectedModel ? {
              brand: selectedModel.brand, model: selectedModel.model, productType: category,
            } : undefined}
            productFields={
              /* The Details' own pickers (owner, 2026-09-25): what is chosen here is what
                 Publish shows prefilled, because both read the same wizard store. */
              <fieldset className="wizard__details" data-testid="case-product">
                <legend>Product</legend>
                <div className="wizard__details-grid">
                  <ProductPickers idPrefix="case-product" categoryTestId="case-product-type"
                    modelTestId="case-product-model" retryTestId="case-product-model-retry"
                    {...pickerProps} />
                </div>
              </fieldset>
            }
            onStageChange={setCaseStageKey}
            onSingleStage={() => wiz.setStep("generate")}
            onMagentaApproved={(image) => {
              // The job this image came from, read at the hand-over and kept ON the image.
              adoptGenerated(image, "case-job", useWizardStore.getState().caseJobId);
              wiz.setStep("mask");
            }}
          />
        )}

        {wiz.step === "generate" && (
          <div className="wizard__step-card">
            <div className="wizard__generate-grid">
              <div className="field">
                <span className="label">Product images</span>
                <ImageStrip
                  files={wiz.productImages}
                  onAdd={pickGenerateImage("product")}
                  onRemove={(i) => wiz.removeProductImage(i)}
                  addLabel="Add image"
                  testid="product"
                />
              </div>
              <div className="field">
                <span className="label">Product reference images</span>
                <ImageStrip
                  files={wiz.referenceImages}
                  onAdd={pickGenerateImage("reference")}
                  onRemove={(i) => wiz.removeReferenceImage(i)}
                  addLabel="Add image"
                  testid="reference"
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="generate-prompt">Instructions</label>
              <textarea
                id="generate-prompt"
                className="input wizard__prompt"
                rows={3}
                value={prompt}
                placeholder="Describe how to combine the reference and product images…"
                onChange={(e) => setPrompt(e.target.value)}
                data-testid="generate-prompt"
              />
              <span className="muted">
                Instructions are required, and sandboxed by the AI guardrails — off-topic or unsafe prompts are rejected.
              </span>
            </div>
            {!selectedModel && (
              <p className="caption" data-testid="generate-product-missing">
                Pick the brand and model on the case photos step to generate.
              </p>
            )}

            <ActionBar label="Generate step actions" primary={
              <button
                type="button"
                className="btn btn--primary"
                onClick={generate}
                disabled={generating || wiz.referenceImages.length === 0 || wiz.productImages.length === 0
                  || !prompt.trim() || !selectedModel}
                data-testid="generate-button"
              >
                {generating ? "Generating…" : "Generate"}
              </button>
            }>
              <button type="button" className="btn btn--back" onClick={() => wiz.setStep("case")} data-testid="generate-back-to-case">
                <Icon name="arrow-left" size={16}/> Back to case photos
              </button>
              {/* WS-AUTOKEY: a customer's flow is Higgsfield-only — a real photo has no magenta
                  marker, and their Mask step has no tool that could cut one out. */}
              {!isCustomer && (
                <button type="button" className="btn btn--ghost" onClick={() => wiz.setStep("upload")}
                  data-testid="generate-upload-manually">
                  Upload manually instead
                </button>
              )}
              <button
                type="button"
                className="btn"
                onClick={continueToMask}
                disabled={!wiz.generated}
                data-testid="continue-to-mask"
              >
                Continue to masking
              </button>
            </ActionBar>

            {generating && (
              <div className="gen-progress" data-testid="gen-progress" role="status" aria-live="polite">
                <div className="gen-progress__head">
                  <span>
                    {elapsed < 3 ? "Sending to model…" : elapsed < 10 ? "Generating…" : "Finalizing…"}
                  </span>
                  <span className="gen-progress__time">{elapsed}s</span>
                </div>
                <div className="gen-progress__track"><div className="gen-progress__bar" /></div>
              </div>
            )}

            {wiz.generated && (
              <div className="wizard__generated">
                <div className="wizard__generated-head">
                  <span className="label">Generated image</span>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={downloadGenerated}
                    data-testid="download-generated"
                  >
                    <Icon name="download" size={16}/> Download
                  </button>
                </div>
                <button
                  type="button"
                  className="wizard__generated-imgbtn"
                  onClick={() => setLightbox(true)}
                  title="Click to enlarge"
                  data-testid="open-lightbox"
                  /* F-8: the ONE seam G-26o opens a dialog through — see `docs/design.md` rule 8. */
                  data-gate-open="lightbox"
                >
                  <img className="wizard__generated-img" src={safeGenerated} alt="Generated result" />
                </button>
              </div>
            )}
          </div>
        )}

        {wiz.step === "upload" && (
          <div className="wizard__step-card">
            <div
              className={`dropzone${dragOver ? " dropzone--over" : ""}`}
              role="button"
              tabIndex={0}
              data-testid="upload-dropzone"
              onClick={pick}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void pick();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (!dragOver) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDropReferences}
            >
              <span className="dropzone__icon" aria-hidden="true"><Icon name="upload" size={16}/></span>
              <strong>Upload reference photos</strong>
              <span className="muted">Click to browse or drag &amp; drop a phone-back and a case-back photo (any image — PNG, JPG, WebP, GIF, TIFF, HEIC… up to 32&nbsp;MB).</span>
            </div>
            {/* No primary: this step commits nothing, it only picks files. A bar with no
                primary is legitimate — it is the BAR that is the rule, not the fill. */}
            <ActionBar label="Upload step actions">
              <button type="button" className="btn btn--back" onClick={() => wiz.setStep("generate")}>
                <Icon name="arrow-left" size={16}/> Back to AI generate
              </button>
            </ActionBar>
          </div>
        )}

        {wiz.step === "mask" && (
          <div className="wizard__step-card">
            {/* F-5 (b) — the geometry problem, at the TOP of the step, carrying its own fix.
                It used to live only as an "Invalid: …" line inside the rail's folded
                "Print area & cutouts" section, so the first signal was a failed Publish. */}
            {!isCustomer && (
              <GeometryProblemBanner
                printArea={wiz.printArea}
                cutouts={wiz.cutouts}
                onFix={(pa, cs) => { wiz.setPrintArea(pa); wiz.setCutouts(cs); setDetectNotice(null); }}
                onRemoveCutout={(i) => wiz.setCutouts(wiz.cutouts.filter((_, n) => n !== i))}
              />
            )}
            {/* F-5 (a) — what the post-detection fallback repaired. `role="status"`: the
                detection SUCCEEDED, and a repair the operator cannot see is a silent change. */}
            {detectNotice && (
              <div className="banner banner--warn" role="status" data-testid="wizard-detect-notice">
                {detectNotice}
              </div>
            )}
            {/* Detect + edit on ONE page: the toolbar runs detection / seeds a default print
                area IN PLACE, the MaskEditor below updates live. It is a TOOLBAR, not the
                step's commit — that is "Submit for approval" / "Publish" in the `.actionbar`
                at the bottom — so neither button is the primary and both stay `.btn--sm`,
                which is what a toolbar action is. The sentence that used to run alongside the
                reference count is the one explanation channel now (rule 10); the COUNT is
                state, and state stays on the canvas. */}
            {/* WS-AUTOKEY (owner decision 4) — the automatic key found no magenta. Every role
                reads the same sentence; the stage-2 retry is offered while the job has one. An
                admin keeps every tool below as well, so the run is never stuck for them. */}
            {fromHiggsfield && (autoKey === "not-found" || autoKey === "off-coverage") && (
              <div className="banner banner--warn row--between row--wrap" role="status"
                aria-busy={keyReport === "checking" || undefined} data-testid="wizard-autokey-failed">
                <ErrorNotice detail={autoKeyReason ?? autoKey} reference={imageReference}
                  advisory={autoKey === "off-coverage"} />
                {keyReport === "failed" && (
                  <button type="button" className="btn btn--sm" onClick={() => reportKey(autoKeyReason)}
                    data-testid="wizard-autokey-check-again">Check again</button>
                )}
                {stage2RetryLeft && (
                  <span className="row row--tight">
                    {/* A customer's Try again is the case card's, which asks first and names the cost. */}
                    <button
                      type="button"
                      className="btn btn--sm"
                      onClick={() => wiz.setStep("stages")}
                      data-testid="wizard-autokey-retry"
                    >
                      {isCustomer ? "Try again" : "Back to stage 2"}
                    </button>
                  </span>
                )}
              </div>
            )}
            {checkNotes.length > 0 && (
              <p className="banner banner--warn" role="status" data-testid="wizard-check-notes">
                {checkNotes.map((n, i) => <ErrorNotice key={i} detail={n} reference={imageReference} advisory />)}
              </p>
            )}
            {retake && (checkNotes.length > 0 || autoKey === "not-found" || autoKey === "off-coverage") && (
              <div className="row row--tight" data-testid="wizard-retake">
                <ConfirmPopover message={retake.question} confirmLabel="Regenerate" onConfirm={retake.regenerate}>
                  {(toggle) => (
                    <button type="button" className="btn btn--sm" onClick={toggle} disabled={busy}
                      data-testid="wizard-regenerate">
                      <Icon name="refresh" size={16} /> Regenerate
                    </button>
                  )}
                </ConfirmPopover>
                <button type="button" className="btn btn--sm btn--ghost" onClick={retake.reject} disabled={busy}
                  data-testid="wizard-reject">
                  Reject
                </button>
              </div>
            )}
            {!isCustomer && (
            <div className="wizard__mask-toolbar">
              <span className="muted">
                <span className="badge badge--info badge--plain">{wiz.references.length}</span> reference
                {wiz.references.length === 1 ? "" : "s"} ready.
              </span>
              <div className="wizard__mask-toolbar-actions">
                <InfoHint text="Auto-detect the print area on this device, or draw it yourself — then fine-tune below." />
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={detect}
                  disabled={busy}
                  data-testid="run-detection"
                >
                  {busy ? "Detecting…" : "Auto-detect (on-device)"}
                </button>
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={drawFromScratch}
                  data-testid="draw-manually"
                >
                  Draw manually
                </button>
              </div>
            </div>
            )}

            {/* P3: catalog-driven Details — Category / Model / Style / Output / Views.
                Persisted on Save (createTemplate + render_settings PATCH + chosen views). */}
            {!isCustomer && (
            <fieldset className="wizard__details" data-testid="wizard-details">
              <legend>Details</legend>
              <div className="wizard__details-grid">
                <label className="field">
                  <span className="label">Category</span>
                  <select
                    className="input"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    data-testid="wizard-category"
                  >
                    {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </label>
                <label className="field">
                  <span className="label">
                    Model
                    {phoneModelsError && (
                      <InlineRetry onRetry={() => void loadPhoneModels()} testId="wizard-model-retry" />
                    )}
                  </span>
                  <InlineCreateRow
                    label="Add a new phone model"
                    fields={[
                      { key: "brand", placeholder: 'Brand (e.g. "Vivo")', ariaLabel: "New model brand" },
                      { key: "model", placeholder: 'Model (e.g. "X200 Pro")', ariaLabel: "New model name" },
                    ]}
                    onSubmit={submitNewModel}
                    onError={(e) => createError(e, "phone model")}
                    testIdBase="wizard-new-model"
                    // review-1 F-3: the mask step commits from the ActionBar's `Publish`. A
                    // filled `Add` here was a second fill on the step, and it disagreed with
                    // the plain `new-style-add` sitting one field below it.
                    submitVariant="plain"
                    edit={selectedModel && {
                      values: { brand: selectedModel.brand, model: selectedModel.model },
                      onSubmit: submitEditModel,
                    }}
                  >
                    {/* WS-B B-2: the shared model label. A naive `${brand} ${model}` join
                        read "iQOO iQOO 15 5G" — AUD-6 top-10 #2. */}
                    <CatalogSelect
                      value={phoneModelId}
                      onChange={(id) => chooseModel(id)}
                      testId="wizard-model"
                      error={phoneModelsError}
                      options={phoneModels.map((p) => ({ value: p.id, label: modelLabel({ brand: p.brand, model: p.model }) }))}
                    />
                  </InlineCreateRow>
                </label>
                <StylePicker value={caseStyleId} onChange={setCaseStyleId} testIdBase="wizard" allowAdd onError={setError} />
                <BaseTypePicker value={baseType} onChange={setBaseType} testIdBase="wizard" allowAdd />
                <label className="field">
                  <span className="label">Name</span>
                  <input
                    className="input"
                    type="text"
                    value={wiz.templateName}
                    onChange={(e) => wiz.setTemplateName(e.target.value, true)}
                    placeholder="Brand and model"
                    data-testid="wizard-name"
                  />
                </label>
                <div className="field">
                  <label className="label" htmlFor="wizard-admin-code">Code</label>
                  <input
                    className="input"
                    type="text"
                    id="wizard-admin-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    ref={codeInputRef}
                    aria-invalid={!!codeRecovery && !codeRecovery.changed || detailsAttempted && isCustomer && !code.trim() || undefined}
                    spellCheck={false}
                    placeholder="Auto-generated if left blank"
                    data-testid="wizard-code"
                    aria-describedby={codeRecovery && !codeRecovery.changed ? "wizard-code-error" : undefined}
                  />
                  {codeRecovery && !codeRecovery.changed && <span id="wizard-code-error" className="field-error" role="alert"><ErrorNotice detail="template code already in use" /></span>}
                  {codeRecoveryControls}
                </div>
                <label className="field">
                  <span className="label">Output format</span>
                  <select
                    className="input"
                    value={outputFormat}
                    onChange={(e) => setOutputFormat(e.target.value)}
                    data-testid="wizard-format"
                  >
                    <option value="">Default (PNG)</option>
                    {OUTPUT_FORMATS.map((f) => (
                      <option key={f} value={f}>{outputFormatLabel(f)}</option>
                    ))}
                  </select>
                </label>
                {backgroundGroup && backgroundGroup.choices.length > 0 && (
                  <label className="field">
                    <span className="label">{backgroundGroup.label}</span>
                    <select
                      className="input"
                      value={background}
                      onChange={(e) => setBackground(e.target.value)}
                      data-testid="wizard-background"
                    >
                      {backgroundGroup.choices.map((c) => (
                        <option key={c.id} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </label>
                )}
                {/* WS-BG: "Solid color" needs a colour. It is saved with the choice
                    (render_settings.background_color) and is what the render paints.
                    F3: a colour swatch follows `docs/design.md`'s rule for one, not the shared
                    field chrome — no `className="input"`, an `aria-label` (a swatch announces
                    nothing on its own) and the value in text beside it. `G-61` pins all three,
                    and this control is why: it shipped breaking two of them. */}
                {background === "solid" && (
                  <div className="field">
                    <span className="label" id="wizard-background-color-label">Background color</span>
                    <div className="row">
                      <input
                        type="color"
                        aria-label="Background color"
                        value={backgroundHex}
                        onChange={(e) => setBackgroundHex(e.target.value)}
                        data-testid="wizard-background-color"
                      />
                      <span className="faint">{backgroundHex}</span>
                    </div>
                  </div>
                )}
              </div>
              {!runMode && <div className="field">
                <span className="label">Views</span>
                <div className="wizard__views" role="group" aria-label="Views">
                  {SELECTABLE_VIEWS.map((v) => (
                    <label key={v} className="field--check wizard__view-check">
                      <input
                        type="checkbox"
                        checked={views.includes(v)}
                        onChange={() => toggleView(v)}
                        data-testid={`wizard-view-${v}`}
                      />
                      <span>{humanize(v)}</span>
                    </label>
                  ))}
                </div>
                {views.length > 1 && (
                  <span className="muted" data-testid="wizard-views-note">
                    Only <strong>{humanize(views.includes("back") ? "back" : views[0])}</strong> is
                    masked here — this wizard has one photo. The other views are created empty; open the template in
                    the editor to add their reference image and masks.
                  </span>
                )}
              </div>}
            </fieldset>
            )}

            {isCustomer && (
              /* Owner decision 2026-09-24 — the Generate page's fields, on the customer's locked
                 step. Same markup family as the admin's Details (`wizard__details`), the same
                 Category select and the same catalog Model select; Brand is the Generate page's
                 own `BrandSelect`. No inline "+ New": a customer cannot write the catalog. */
              <fieldset ref={detailsRef} className="wizard__details" data-testid="wizard-details-customer">
                <legend>Details</legend>
                <div className="wizard__details-grid">
                  <ProductPickers idPrefix="wizard-c" categoryTestId="wizard-category"
                    modelTestId="wizard-model" retryTestId="wizard-model-retry" {...pickerProps} />
                  <div className="field">
                    <label className="label" htmlFor="wizard-c-code">SKU</label>
                    <input
                      id="wizard-c-code"
                      aria-label="SKU"
                      className="input"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      ref={codeInputRef}
                      aria-invalid={!!codeRecovery && !codeRecovery.changed || detailsAttempted && isCustomer && !code.trim() || undefined}
                      spellCheck={false}
                      placeholder="e.g. CASE-PRO"
                      data-testid="wizard-code"
                      aria-describedby={codeRecovery && !codeRecovery.changed || detailsAttempted && !code.trim() ? "wizard-code-error" : undefined}
                    />
                    {codeRecovery && !codeRecovery.changed ? <span id="wizard-code-error" className="field-error" role="alert"><ErrorNotice detail="template code already in use" /></span>
                      : detailsAttempted && !code.trim() && <RequiredFieldError id="wizard-code-error" field="SKU" />}
                    {codeRecoveryControls}
                  </div>
                  <label className="field" htmlFor="wizard-c-name">
                    <span className="label">Name</span>
                    <input
                      id="wizard-c-name"
                      aria-label="Name"
                      className="input"
                      type="text"
                      value={wiz.templateName}
                      onChange={(e) => wiz.setTemplateName(e.target.value, true)}
                      placeholder="Brand and model"
                      data-testid="wizard-name"
                    />
                  </label>
                </div>
              </fieldset>
            )}

            {runMode && (
              <ul className="wizard__view-thumbs" aria-label="Views" data-testid="wizard-view-thumbs">
                {runViews.map((rv) => (
                  <ViewThumb key={rv.view} run={rv} pressed={rv.view === wiz.activeView}
                    keyed={viewKeys[rv.view]?.verdict} onPick={() => showView(rv.view)} />
                ))}
              </ul>
            )}
            {/* Veiled, not hidden: the automatic key reads the laid-out canvas, which
                `display: none` would give no size. */}
            {runMode ? (
              <div className="wizard__canvases">
                {runViews.filter((rv) => rv.view === wiz.activeView || rv.view === publishView || shownViews.includes(rv.view)).map((rv) => (
                  <RunViewCanvas key={rv.view} run={rv} active={rv.view === wiz.activeView} veiled={hideFromCustomer}
                    editor={runEditorProps(rv)} />
                ))}
              </div>
            ) : (
              <div className="wizard__canvas" data-veiled={hideFromCustomer} aria-hidden={hideFromCustomer || undefined}
                data-testid="wizard-canvas">
              <MaskEditor
                engine={engine}
                logger={logger}
                caseImageUrl={caseImageUrl}
                printArea={wiz.printArea}
                cutouts={wiz.cutouts}
                onPrintAreaChange={wiz.setPrintArea}
                onCutoutsChange={wiz.setCutouts}
                brandArea={brandArea}
                onBrandAreaChange={setBrandArea}
                onNaturalDims={setBaseDims}
                baseType={baseType}
                // Emit-only: the version doesn't exist until submit, so MaskEditor bakes WITHOUT a
                // versionId and just hands the latest payload up. submit() uploads it afterwards.
                // F4 — `bakeRef` lets submit() force a fresh bake right before that upload, so a
                // slider/calibration change made AFTER the last emit is re-composed with the current
                // params instead of uploading pre-slider pixels.
                bakeRef={bakeRef}
                // WS-BG F2: the live preview composites on the template's chosen background.
                templateBackgroundColor={previewBackgroundColor}
                onBaked={(payload) => { bakedPayloadRef.current = payload; }}
                // WS-AUTOKEY — decisions 1-3: a Higgsfield image keys itself; a customer gets
                // placement, the test mock-up and Product, an admin every tool. It starts on the
                // Reference finish like a photo (owner, 2026-10-02: Flat did not look printed on).
                autoMagentaKey={fromHiggsfield}
                shadedMagentaGuide={fromHiggsfield}
                initialPrintLight="reference"
                onAutoMagentaKey={onAutoMagentaKey}
                onCaseImageError={isCustomer && fromHiggsfield ? keyUnanswered : undefined}
                coverageHeightPct={wiz.generated?.productHeightPct}
                clearCaseImage={wiz.generated?.clear?.bytes}
                railMode={railMode}
                adminRailItems={brandLogoRail}
              />
              </div>
            )}

            {!isCustomer && !fromGeneratePage && brandLogoToggle}

            {/* F-1: the publish was REFUSED because the baked mock-up could not be published.
                Nothing was submitted — the version is still a Draft holding every setting on
                this screen — so these banners carry the one action that can finish the run.
                Same vocabulary, tone and affordances as `TemplateEditor`'s pair: `banner--warn`,
                `role="status"`, sentence case, a Retry, and NO Dismiss on the hosting-off one
                (hiding a state the operator cannot otherwise discover is how this stayed
                invisible). */}
            {assetSync === "blocked" && (
              <div
                className="banner banner--warn row--between row--wrap"
                role="status"
                data-testid="wizard-baked-sync-blocked"
              >
                <span>
                  This template was not published. Cross-device mock-ups are off for this
                  workspace, so its mock-up cannot be uploaded and other devices would render it
                  design-only.{" "}
                  {canAccessAdmin(viewerRole)
                    ? "Turn on hosting in Settings → Hosting, then press Retry."
                    : "Ask a platform admin to turn on hosting in Settings → Hosting, then press Retry."}{" "}
                  {REFUSAL_SURVIVES_COPY}
                </span>
                <span className="row row--tight">
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={retryPublish}
                    disabled={busy}
                    data-testid="wizard-baked-sync-retry"
                  >
                    {busy ? "Loading…" : "Retry"}
                  </button>
                </span>
              </div>
            )}
            {(assetSync === "failed" || assetSync === "nobake") && (
              <div
                className="banner banner--warn row--between row--wrap"
                role="status"
                data-testid="wizard-baked-sync-failed"
              >
                <span>
                  {assetSync === "nobake"
                    // F-c: nothing was uploaded because nothing could be BUILT. Naming an upload
                    // here would send the operator to check a network that was never used.
                    ? "This template was not published — the mock-up could not be prepared on this device."
                    // F-e: cause-NEUTRAL, like the editor's twin. A presign 409 is not a network
                    // fault, and "check your network" sends the operator to the wrong place.
                    : "This template was not published — its mock-up could not be uploaded."}
                  {bakedSyncError && (
                    <>
                      {" "}
                      <span data-testid="wizard-baked-sync-reason"><ErrorNotice detail={bakedSyncError} /></span>
                    </>
                  )}{" "}
                  {REFUSAL_SURVIVES_COPY}
                </span>
                <span className="row row--tight">
                  <button
                    type="button"
                    className="btn btn--sm"
                    onClick={retryPublish}
                    disabled={busy}
                    data-testid="wizard-baked-sync-retry"
                  >
                    {busy ? "Loading…" : "Retry"}
                  </button>
                </span>
              </div>
            )}

            <ActionBar label="Mask step actions" primary={
              <button
                type="button"
                className="btn btn--primary"
                onClick={submit}
                // WS-AUTOKEY: a customer has no tool to make a print area, so there is nothing to
                // publish until the automatic key has found the magenta one. Missing Details
                // are explained beside their fields when Publish is pressed.
                disabled={busy || customerAwaitsKey}
                data-testid="submit-for-approval"
              >
                {busy ? "Saving…" : approvalsEnabled ? "Submit for approval" : "Publish"}
              </button>
            }>
              {/* ED-11: the mask step's only exit was Cancel, which DISCARDS the whole run. This
                  Back just changes the step — every field, image and mask lives in the wizard
                  store, so nothing is lost and returning here restores it exactly. */}
              <button
                type="button"
                className="btn btn--back"
                onClick={() => (fromGeneratePage ? wiz.setOpen(false)
                  : wiz.setStep(wiz.generated ? "generate" : "upload"))}
                disabled={busy}
                data-testid="wizard-back-to-generate"
              >
                <Icon name="arrow-left" size={16}/> Back{fromGeneratePage ? " to Generate"
                  : wiz.generated ? " to AI generate" : " to upload"}
              </button>
              {assetSync === "uploading" && (
                <span className="wizard__asset-sync" data-testid="wizard-asset-uploading">
                  Uploading template assets…
                </span>
              )}
              {missingSentence && (
                <span className="wizard__asset-sync" role="status" data-testid="wizard-details-missing">
                  {missingSentence}
                </span>
              )}
              {customerAwaitsKey && autoKey === "pending" && (
                <span className="wizard__asset-sync" role="status" data-testid="wizard-autokey-pending">
                  {isCustomer ? "Preparing your transparent mock-up…" : "Finding the print area…"}
                </span>
              )}
            </ActionBar>
          </div>
        )}

        {wiz.step === "submit" && (() => {
          // Copy derives from the STATUS the server returned — never the local approvals guess.
          const copy = saveStatusCopy(submittedStatus ?? "Active");
          return (
          <div className="wizard__success" data-testid="wizard-success">
            <span className="wizard__success-icon" aria-hidden="true"><Icon name="check" size={16}/></span>
            <h3>{copy.headline}</h3>
            <span className={copy.chipClass} data-testid="wizard-status-chip">{copy.chipLabel}</span>
            <p>{copy.detail}</p>
            {assetSync === "warned" && (
              <p className="wizard__asset-warning" role="status" data-testid="wizard-asset-warning">
                Template saved, but its assets didn’t sync — open it in the editor to retry.
              </p>
            )}
            {caseImageWarned && (
              <p className="wizard__asset-warning" role="status" data-testid="wizard-caseimg-warning">
                Saved, but the case photo couldn’t be stored on this device — re-open the template to re-attach it.
              </p>
            )}
            {noBakeWarned && (
              <p className="wizard__asset-warning" role="status" data-testid="wizard-nobake-warning">
                Saved, but this template has no mock-up — the case photo could not be loaded here, so every
                device would export the design on its own. Open it in the editor once the photo loads and Save.
              </p>
            )}
            {brandColorWarned && (
              <p className="wizard__asset-warning" role="status" data-testid="wizard-brandcolor-warning">
                Saved — but your default brand has no primary colour, so the Brand color background
                renders white. Set one in Branding (or make the brand you want the default), then
                open the template in the editor and Save.
              </p>
            )}
            {casePublishWarned && (
              <p className="wizard__asset-warning" role="status" data-testid="wizard-casepub-warning">
                Saved, but the case photo couldn’t be published to hosting — the template will open BLANK on other
                systems. Open it in the editor and Save (with working hosting) to publish it.
              </p>
            )}
            <ActionBar label="Submit step actions" primary={
              onDone ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => {
                    // ED-11: the store SURVIVES a remount by design, and `onDone` never reset it —
                    // so re-opening the wizard came back into the finished run (its images, prompt,
                    // masks and template/version ids all still loaded), and the next "Publish"
                    // resubmitted the LOCKED version. Clear it on the way out, exactly like the
                    // standalone "Start another" path below.
                    bakedPayloadRef.current = null;
                    setAssetSync("idle");
                    setBakedSyncError(null);
                    setCaseImageWarned(false);
                    setCasePublishWarned(false);
                    setSubmittedStatus(null);
                    wiz.reset();
                    onDone();
                  }}
                  data-testid="wizard-done"
                >
                  Back to templates
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => {
                    // Drop the previous run's baked payload so it can't upload to the NEXT template
                    // if the user saves the new one without previewing a mockup again.
                    bakedPayloadRef.current = null;
                    setAssetSync("idle");
                    setBakedSyncError(null);
                    setCaseImageWarned(false);
                    setSubmittedStatus(null);
                    wiz.reset();
                  }}
                >
                  Start another
                </button>
              )
            } />
          </div>
          );
        })()}
      </div>

      {/* WS-Z8 — ADOPTED. This was the last hand-rolled `.lightbox`, deferred at WS-Z2 for one
          stated reason: a full-bleed image viewer needs a chrome-less variant, and `ui/Modal`
          only drew titled panels with a `.modal__head`. WS-Z7 shipped that variant, so the
          block is the primitive now — `bare`, whose `label` is required exactly where `title`
          is forbidden, because `aria-modal` with no name announces the word "dialog" and a
          bare dialog has no heading to point at.

          The viewer gains what the hand-rolled one never had: Escape, a focus trap, focus on
          open, focus back to the thumbnail on close, and a place in the shared stacking order.
          It keeps what it had: the backdrop closes it, a click on the image does not (the
          overlay is its own element now, so no `stopPropagation` is needed), and the image is
          a direct child of the body, where `modal.css`'s `> img` cap sizes it — so the viewer
          needs no sizing CSS of its own and `.lightbox*` leaves `styles/components.css`. */}
      {lightbox && wiz.generated && (
        <Modal bare label="Generated image preview" testid="lightbox" onClose={() => setLightbox(false)}>
          <img src={safeGenerated} alt="Generated result (enlarged)" />
        </Modal>
      )}
    </section>
  );
}
